import { TRenewVisaEmployeeApplicationDocument } from "@/api/entities/renew-visa-employee-application-document.entity";
import { TRenewVisaUploadFileDocument } from "@/api/entities/renew-visa-upload-file-document.entity";
import { useGetRenewVisaBasicInformationDocumentGroupInfo, useGetRenewVisaDocumentGroupInfo } from "@/api/services/main/renew-visa";
import { API_UNKNOWN_ERROR_MESSAGE } from "@/constants/common";
import { TRenewVisaUploadFileForm } from "@/features/renew-visa/types/renewVisaUploadFileForm";
import { findRenewVisaDocumentStepGroup } from "@/features/renew-visa/utils/findDocumentStepGroup";
import {
  COMPANY_CONDITION,
  COMPANY_DIVISION,
  COMPANY_SPECIFIC_INDUSTRY_NAME,
  DOCUMENT_STEP_KEYS,
  RESIDENCE_STATUS,
  TCompanySpecificIndustryName,
} from "@/types/enum";
import { generateFileFromFilePath } from "@/utils/export";
import { mergePdfs } from "@/utils/file";
import { toNumber } from "@/utils/number";
import { getFullName } from "@/utils/string";
import { toast } from "@/utils/toast";
import fontkit from "@pdf-lib/fontkit";
import { BlendMode, breakTextIntoLines, PDFDocument, rgb } from "pdf-lib";
import { useEffect, useState } from "react";

export type TMergeRenewVisaFile = {
  order: number;
  key: keyof TRenewVisaUploadFileForm;
  filePath?: string | string[] | null;
  existed?: boolean | null;
  input?: string | null;
};

type TCheckFileField = {
  key: keyof (TRenewVisaUploadFileForm & { unknown?: string | null });
  fixedValue?: "yes" | "no";
  companySpecificIndustryName?: TCompanySpecificIndustryName;
  page: number;
  yes: { x: number; y: number; size: number };
  no: { x: number; y: number; size: number };
  inputArea?: { x: number; y: number; height: number; width: number };
  children?: (keyof (TRenewVisaUploadFileForm & { unknown?: string | null }))[];
};

type TCheckFileFieldResult = TCheckFileField & { value?: "yes" | "no"; input?: string | null };

type TTableKey = "table_1" | "table_2_1" | "table_2_2" | "table_2_3" | "table_3_1" | "table_3_2" | "table_3_3";

export const useMergeRenewVisaFile = () => {
  const { data: renewVisaDocumentGroup } = useGetRenewVisaDocumentGroupInfo();

  const company = renewVisaDocumentGroup?.company;

  const documentGroupStep1 = findRenewVisaDocumentStepGroup(renewVisaDocumentGroup, DOCUMENT_STEP_KEYS.RENEW_VISA_GROUP_BASIC_INFORMATION);

  const { data: basicInfoData } = useGetRenewVisaBasicInformationDocumentGroupInfo({
    renewVisaDocumentStepGroupId: toNumber(documentGroupStep1?.id),
  });

  const [fontBytes, setFontBytes] = useState<ArrayBuffer>();
  const [renewVisaCoverPageArrayBuffer, setRenewVisaCoverPageArrayBuffer] = useState<ArrayBuffer>();

  // Fetch font once when component mounts
  useEffect(() => {
    const loadFont = async () => {
      try {
        const fontResponse = await fetch("/fonts/NotoSansJP-Regular.ttf");
        const bytes = await fontResponse.arrayBuffer();
        setFontBytes(bytes);
      } catch (error) {
        console.error("Error fetching font:", error);
      }
    };
    const loadRenewVisaCoverPage = async () => {
      try {
        const coverPageResponse = await fetch("/pdf/renew_visa_cover_page.pdf");
        const arrayBuffer = await coverPageResponse.arrayBuffer();
        setRenewVisaCoverPageArrayBuffer(arrayBuffer);
      } catch (error) {
        console.error("Error fetching cover page:", error);
      }
    };
    loadFont();
    loadRenewVisaCoverPage();
  }, []);

  const handleGetFilesGroupByTable = async (
    renewVisaEmployeeApplication: TRenewVisaEmployeeApplicationDocument | undefined,
    uploadFileData: TRenewVisaUploadFileDocument | undefined,
  ) => {
    const foreignWorkerCompensationStatementFile = uploadFileData?.foreignWorkerCompensationStatementFilePaths;

    const employmentContractCopyFile = uploadFileData?.employmentContractCopyFilePath;

    const employmentConditionsCopyFile = uploadFileData?.employmentConditionsCopyFilePaths;

    const wagePaymentFile = uploadFileData?.wagePaymentFilePath;

    const applicantListFile = uploadFileData?.applicantListFilePath;

    const annualCalendarWithLanguageFile = uploadFileData?.annualCalendarWithLanguageFilePath;

    const annualVariableWorkAgreementFile = uploadFileData?.annualVariableWorkAgreementFilePath;

    const applicantIndividualResidentTaxCertFile = uploadFileData?.applicantIndividualResidentTaxCertFilePath;

    const applicantResidentTaxPaymentCertFile = uploadFileData?.applicantResidentTaxPaymentCertFilePath;

    const applicantWithholdingSlipCopyFile = uploadFileData?.applicantWithholdingSlipCopyFilePath;

    const applicantHealthInsuranceCardCopyFile = uploadFileData?.applicantHealthInsuranceCardCopyFilePath;

    const applicantHealthInsurancePaymentCertFile = uploadFileData?.applicantHealthInsurancePaymentCertFilePath;

    const applicantHealthInsuranceRecordInquiryResponseFile = uploadFileData?.applicantHealthInsuranceRecordInquiryResponseFilePath;

    const applicantPensionReceiptCopy1File = uploadFileData?.applicantPensionReceiptCopy1FilePath;

    const applicantPensionRecordInquiry2File = uploadFileData?.applicantPensionRecordInquiry2FilePath;

    const previousApplicationDocumentsFile = uploadFileData?.previousApplicationDocumentsFilePath;

    const publicDutyPledgeFile = uploadFileData?.publicDutyPledgeFilePath;

    const publicDutyComplianceFilePath = uploadFileData?.publicDutyComplianceFilePath;

    const quarterlyReportCopyFile = uploadFileData?.quarterlyReportCopyFilePath;

    const innovationCompanyProofFile = uploadFileData?.innovationCompanyProofFilePath;

    const certifiedCompanyProofFile = uploadFileData?.certifiedCompanyProofFilePath;

    const annualSalaryReportCopyFile = uploadFileData?.annualSalaryReportCopyFilePath;

    const immigrationElectronicSystemPledgeFile = uploadFileData?.immigrationElectronicSystemPledgeFilePath;

    const documentOmissionPledgeFile = uploadFileData?.documentOmissionPledgeFilePath;

    // const specificSkillsInstitutionOverviewDocumentFile = uploadFileData?.specificSkillsInstitutionOverviewDocumentFilePath;

    // const registrationCertFile = uploadFileData?.registrationCertFilePath;

    // const executiveResidentRecordCopyFile = uploadFileData?.executiveResidentRecordCopyFilePath;

    // const specificSkillsInstitutionOfficerAffidavitFile = uploadFileData?.specificSkillsInstitutionOfficerAffidavitFilePath;

    const laborInsuranceNoDelegationFile = uploadFileData?.laborInsuranceNoDelegationFilePath;

    const laborInsuranceDelegationFile = uploadFileData?.laborInsuranceDelegationFilePath;

    const socialInsurancePaymentStatusCertFile = uploadFileData?.socialInsurancePaymentStatusCertFilePath;

    const taxOfficePaymentCertNo3File = uploadFileData?.taxOfficePaymentCertNo3FilePath;

    const corporateResidentTaxPaymentCertFile = uploadFileData?.corporateResidentTaxPaymentCertFilePath;

    // const individualBusinessOwnerResidentCertFile = uploadFileData?.individualBusinessOwnerResidentCertFilePath;

    const healthInsuranceWelfarePensionCertFile = uploadFileData?.healthInsuranceWelfarePensionCertFilePath;

    const nonHealthInsuranceWelfarePensionCertFile = uploadFileData?.nonHealthInsuranceWelfarePensionCertFilePath;

    const individualHealthInsurancePaymentCertFile = uploadFileData?.individualHealthInsurancePaymentCertFilePath;

    const individualPensionReceiptCopyFile = uploadFileData?.individualPensionReceiptCopyFilePath;

    const individualBusinessOwnerTaxOfficePaymentCertNo3File = uploadFileData?.individualBusinessOwnerTaxOfficePaymentCertNo3FilePath;

    const individualResidentTaxCertFile = uploadFileData?.individualResidentTaxCertFilePath;

    const careWorkplaceOverviewFile = uploadFileData?.careWorkplaceOverviewFilePath;

    const councilMembershipCertFile = uploadFileData?.councilMembershipCertFilePath;

    const hotelBusinessLicenseFile = uploadFileData?.hotelBusinessLicenseFilePath;

    const specificSkillOrganizationOverviewFile = uploadFileData?.specificSkillOrganizationOverviewFilePath;

    const specifiedSkillForeignWorkerPledgeFile = uploadFileData?.specifiedSkillForeignWorkerPledgeFilePath;

    const registeredCorporateStructureListPrintedFile = uploadFileData?.registeredCorporateStructureListPrintedFilePath;

    const constructionSpecifiedSkillAcceptancePlanCertFile = uploadFileData?.constructionSpecifiedSkillAcceptancePlanCertFilePath;

    const buildingEnvHealthMgmtRegistrationCertFile = uploadFileData?.buildingEnvHealthMgmtRegistrationCertFilePath;

    const shipbuildingIndustryConfirmationFile = uploadFileData?.shipbuildingIndustryConfirmationFilePath;

    const businessLicenseOrNotificationFormFile = uploadFileData?.businessLicenseOrNotificationFormFilePath;

    const specifiedSkillAutomobileMaintenanceCertFile = uploadFileData?.specifiedSkillAutomobileMaintenanceCertFilePath;

    const specifiedSkillAutomobileMaintenanceIssuranceFile = uploadFileData?.specifiedSkillAutomobileMaintenanceIssuranceFilePath;

    const fishingLicenseCopy1File = uploadFileData?.fishingLicenseCopy1FilePath;

    const fishingLicenseCopy2File = uploadFileData?.fishingLicenseCopy2FilePath;

    const fishingLicenseCopy3File = uploadFileData?.fishingLicenseCopy3FilePath;

    const fishingCooperativeRightsDocumentFile = uploadFileData?.fishingCooperativeRightsDocumentFilePath;

    const fishingCooperativeMembershipDocumentFile = uploadFileData?.fishingCooperativeMembershipDocumentFilePath;

    const fishingBoatRegistryCopyFile = uploadFileData?.fishingBoatRegistryCopyFilePath;

    const fishingBoatRegistrationCertCopyFile = uploadFileData?.fishingBoatRegistrationCertCopyFilePath;

    const ryokanBusinessLicenseFile = uploadFileData?.ryokanBusinessLicenseFilePath;

    const adultEntertainmentBusinessLicenseFile = uploadFileData?.adultEntertainmentBusinessLicenseFilePath;

    const table1Files: TMergeRenewVisaFile[] = [
      // 特定技能外国人の在留諸申請に係る提出書類一覧表(第１表)
      // order: 1,

      // 在留期間更新許可申請書
      // {
      //   order: 2,
      //   key: "unkown",
      // },

      // 申請人名簿
      {
        order: 2,
        key: "applicantListFilePath",
        filePath: applicantListFile,
      },

      // 特定技能外国人の報酬に関する説明書
      {
        order: 3,
        key: "foreignWorkerCompensationStatementFilePaths",
        filePath: foreignWorkerCompensationStatementFile,
        input: uploadFileData?.foreignWorkerCompensationStatementInput,
      },

      // 特定技能雇用契約書の写し
      {
        order: 4,
        key: "employmentContractCopyFilePath",
        filePath: employmentContractCopyFile,
        existed: uploadFileData?.isExistEmploymentContractCopyFilePath,
      },

      // 雇用条件書の写し
      {
        order: 5,
        key: "employmentConditionsCopyFilePaths",
        filePath: employmentConditionsCopyFile,
        existed: uploadFileData?.isExistEmploymentConditionsCopyFilePaths,
      },

      // 金の支払
      {
        order: 6,
        key: "wagePaymentFilePath",
        filePath: wagePaymentFile,
        existed: uploadFileData?.isExistWagePaymentFilePath,
      },

      // 申請人が十分に理解できる言語が併記された年間カレンダーの写し
      {
        order: 6.1,
        key: "annualCalendarWithLanguageFilePath",
        filePath: annualCalendarWithLanguageFile,
      },

      // １年単位の変形労働時間制に関する協定書の写し
      {
        order: 6.2,
        key: "annualVariableWorkAgreementFilePath",
        filePath: annualVariableWorkAgreementFile,
      },

      // 申請人の個人住民税の課税証明書
      {
        order: 7,
        key: "applicantIndividualResidentTaxCertFilePath",
        filePath: applicantIndividualResidentTaxCertFile,
        input: uploadFileData?.applicantIndividualResidentTaxCertInput,
      },

      // 申請人の住民税の納税証明書
      {
        order: 8,
        key: "applicantResidentTaxPaymentCertFilePath",
        filePath: applicantResidentTaxPaymentCertFile,
        input: uploadFileData?.applicantResidentTaxPaymentCertInput,
      },

      // 申請人の給与所得の源泉徴収票の写し
      {
        order: 9,
        key: "applicantWithholdingSlipCopyFilePath",
        filePath: applicantWithholdingSlipCopyFile,
        input: uploadFileData?.applicantWithholdingSlipCopyInput,
      },

      // 申請人の国民健康保険被保険者証の写し
      {
        order: 10,
        key: "applicantHealthInsuranceCardCopyFilePath",
        filePath: applicantHealthInsuranceCardCopyFile,
        input: uploadFileData?.applicantHealthInsuranceCardCopyInput,
      },

      // 申請人の国民健康保険料(税)納付証明書
      {
        order: 11,
        key: "applicantHealthInsurancePaymentCertFilePath",
        filePath: applicantHealthInsurancePaymentCertFile,
        input: uploadFileData?.applicantHealthInsurancePaymentCertInput,
      },

      // 申請人の被保険者記録照会回答票
      {
        order: 11.5,
        key: "applicantHealthInsuranceRecordInquiryResponseFilePath",
        filePath: applicantHealthInsuranceRecordInquiryResponseFile,
        input: uploadFileData?.applicantHealthInsuranceRecordInquiryResponseInput,
      },

      // ①申請人の国民年金保険料領収証書の写し
      {
        order: 12,
        key: "applicantPensionReceiptCopy1FilePath",
        filePath: applicantPensionReceiptCopy1File,
        input: uploadFileData?.applicantPensionReceiptCopy1Input,
      },

      // ②申請人の被保険者記録照会(納付Ⅱ)(被保険者記録照会回答票を含む。)
      {
        order: 13,
        key: "applicantPensionRecordInquiry2FilePath",
        filePath: applicantPensionRecordInquiry2File,
        input: uploadFileData?.applicantPensionRecordInquiry2Input,
      },

      // 前回申請時に履行すべきであった公的義務に係る書類
      {
        order: 14,
        key: "previousApplicationDocumentsFilePath",
        filePath: previousApplicationDocumentsFile,
      },

      // 公的義務履行に関する誓約書
      {
        order: 15,
        key: "publicDutyPledgeFilePath",
        filePath: publicDutyPledgeFile,
      },
    ];

    const table2And1Files: TMergeRenewVisaFile[] = [
      // 特定技能外国人の在留諸申請に係る提出書類一覧表(第2表の１)
      // order: 16,

      // 四季報の写し又は日本の証券取引所に上場していることを証明する文書（写し）
      {
        order: 17,
        key: "quarterlyReportCopyFilePath",
        filePath: quarterlyReportCopyFile,
      },

      // 高度専門職省令第１条第１項各号の表の特別加算の項の中欄イ又はロの対象企業（イノベーション創出企業）であることを証明する文書
      {
        order: 18,
        key: "innovationCompanyProofFilePath",
        filePath: innovationCompanyProofFile,
      },

      // 「一定の条件を満た企業等」であることを証明する文書（例えば、認定証等の写し）
      {
        order: 19,
        key: "certifiedCompanyProofFilePath",
        filePath: certifiedCompanyProofFile,
      },

      // 前年分の職員の給与所得の源泉徴収票等の法定調書合計表（受付印のあるものの写し）
      {
        order: 20,
        key: "annualSalaryReportCopyFilePath",
        filePath: annualSalaryReportCopyFile,
      },

      // 出入国在留管理庁電子届出システムに関する誓約書
      {
        order: 21,
        key: "immigrationElectronicSystemPledgeFilePath",
        filePath: immigrationElectronicSystemPledgeFile,
      },

      // 書類の省略に当たっての誓約書
      {
        order: 22,
        key: "documentOmissionPledgeFilePath",
        filePath: documentOmissionPledgeFile,
      },
    ];

    const table2And2Files: TMergeRenewVisaFile[] = [
      // 特定技能所属機関概要書
      // {
      //   key: "specificSkillsInstitutionOverviewDocumentFilePath",
      //   order: 23,
      //   filePath: specificSkillsInstitutionOverviewDocumentFile,
      //   input: uploadFileData?.specificSkillsInstitutionOverviewDocumentInput,
      // },

      // 特定技能所属機関概要書
      // {
      //   order: 24,
      //   key: "specificSkillsInstitutionOverviewDocumentFilePath",
      //   filePath: specificSkillsInstitutionOverviewDocumentFile,
      // },

      // 登記事項証明書
      // {
      //   order: 25,
      //   key: "registrationCertFilePath",
      //   filePath: registrationCertFile,
      //   input: uploadFileData?.registrationCertInput,
      // },

      // 業務執行に関与する役員の住民票の写し
      // {
      //   order: 26,
      //   key: "executiveResidentRecordCopyFilePath",
      //   filePath: executiveResidentRecordCopyFile,
      //   input: uploadFileData?.executiveResidentRecordCopyInput,
      // },

      // 特定技能所属機関の役員に関する誓約書
      // {
      //   order: 27,
      //   key: "specificSkillsInstitutionOfficerAffidavitFilePath",
      //   filePath: specificSkillsInstitutionOfficerAffidavitFile,
      //   input: uploadFileData?.specificSkillsInstitutionOfficerAffidavitInput,
      // },

      // 労働保険事務組合に事務委託していない場合
      {
        order: 28,
        key: "laborInsuranceNoDelegationFilePath",
        filePath: laborInsuranceNoDelegationFile,
        input: uploadFileData?.laborInsuranceNoDelegationInput,
      },

      // 労働保険事務組合に事務委託している場合
      {
        order: 29,
        key: "laborInsuranceDelegationFilePath",
        filePath: laborInsuranceDelegationFile,
        input: uploadFileData?.laborInsuranceDelegationInput,
      },

      // 社会保険料納入状況回答票又は健康保険・厚生年金保険料領収証書の写し
      {
        order: 30,
        key: "socialInsurancePaymentStatusCertFilePath",
        filePath: socialInsurancePaymentStatusCertFile,
        input: uploadFileData?.socialInsurancePaymentStatusCertInput,
      },

      // 税務署発行の納税証明書(その３)
      {
        order: 31,
        key: "taxOfficePaymentCertNo3FilePath",
        filePath: taxOfficePaymentCertNo3File,
        input: uploadFileData?.taxOfficePaymentCertNo3Input,
      },

      // 法人住民税の市町村発行の納税証明書
      {
        order: 32,
        key: "corporateResidentTaxPaymentCertFilePath",
        filePath: corporateResidentTaxPaymentCertFile,
        input: uploadFileData?.corporateResidentTaxPaymentCertInput,
      },

      // 公的義務履行に関する説明書
      {
        order: 33,
        key: "publicDutyComplianceFilePath",
        filePath: publicDutyComplianceFilePath,
      },
    ];

    const table2And3Files: TMergeRenewVisaFile[] = [
      // 特定技能外国人の在留諸申請に係る提出書類一覧表(第2表の3)
      // order: 34,

      // 特定技能所属機関概要書
      // {
      //   order: 35,
      //   key: "specificSkillsInstitutionOverviewDocumentFilePath",
      //   filePath: specificSkillsInstitutionOverviewDocumentFile,
      //   input: uploadFileData?.specificSkillsInstitutionOverviewDocumentInput,
      // },

      // 個人事業主の住民票の写し
      // {
      //   order: 36,
      //   key: "individualBusinessOwnerResidentCertFilePath",
      //   filePath: individualBusinessOwnerResidentCertFile,
      //   input: uploadFileData?.individualBusinessOwnerResidentCertInput,
      // },

      // 労働保険事務組合に事務委託していない場合
      {
        order: 37,
        key: "laborInsuranceNoDelegationFilePath",
        filePath: laborInsuranceNoDelegationFile,
        input: uploadFileData?.laborInsuranceNoDelegationInput,
      },

      // 労働保険事務組合に事務委託している場合
      {
        order: 38,
        key: "laborInsuranceDelegationFilePath",
        filePath: laborInsuranceDelegationFile,
        input: uploadFileData?.laborInsuranceDelegationInput,
      },

      // 健康保険・厚生年金保険の適用事業所の場合
      {
        order: 39,
        key: "healthInsuranceWelfarePensionCertFilePath",
        filePath: healthInsuranceWelfarePensionCertFile,
        input: uploadFileData?.healthInsuranceWelfarePensionCertInput,
      },

      // 健康保険・厚生年金保険の適用事業所でない場合
      {
        order: 40,
        key: "nonHealthInsuranceWelfarePensionCertFilePath",
        filePath: nonHealthInsuranceWelfarePensionCertFile,
        input: uploadFileData?.nonHealthInsuranceWelfarePensionCertInput,
      },

      // 個人事業主の国民健康保険料(税)納付証明書
      {
        order: 41,
        key: "individualHealthInsurancePaymentCertFilePath",
        filePath: individualHealthInsurancePaymentCertFile,
        input: uploadFileData?.individualHealthInsurancePaymentCertInput,
      },

      // 個人事業主の国民年金保険料領収証書の写し又は被保険者記録照会(納付Ⅱ)
      {
        order: 42,
        key: "individualPensionReceiptCopyFilePath",
        filePath: individualPensionReceiptCopyFile,
        input: uploadFileData?.individualPensionReceiptCopyInput,
      },

      // 個人事業主の税務署発行の納税証明書(その３)
      {
        order: 43,
        key: "individualBusinessOwnerTaxOfficePaymentCertNo3FilePath",
        filePath: individualBusinessOwnerTaxOfficePaymentCertNo3File,
        input: uploadFileData?.individualBusinessOwnerTaxOfficePaymentCertNo3Input,
      },

      // 個人事業主の個人住民税の市町村発行の納税証明書
      {
        order: 44,
        key: "individualResidentTaxCertFilePath",
        filePath: individualResidentTaxCertFile,
        input: uploadFileData?.individualResidentTaxCertInput,
      },

      // 公的義務履行に関する説明書
      {
        order: 45,
        key: "publicDutyComplianceFilePath",
        filePath: publicDutyComplianceFilePath,
      },
    ];

    const table3And1Files: TMergeRenewVisaFile[] = [
      // 特定技能外国人の在留諸申請に係る提出書類一覧表(第3表の1)
      // order: 46,

      // 介護分野における業務を行わせる事業所の概要書
      {
        order: 47,
        key: "careWorkplaceOverviewFilePath",
        filePath: careWorkplaceOverviewFile,
      },

      // 協議会の構成員であることの証明書
      {
        order: 48,
        key: "councilMembershipCertFilePath",
        filePath: councilMembershipCertFile,
      },

      // ①建築物清掃業登録証明書
      {
        order: 49,
        key: "specificSkillOrganizationOverviewFilePath",
        filePath: specificSkillOrganizationOverviewFile,
        input: uploadFileData?.specificSkillOrganizationOverviewInput,
      },

      // （参考様式3-1）特定技能外国人の受入れに関する誓約書
      {
        order: 49.1,
        key: "specifiedSkillForeignWorkerPledgeFilePath",
        filePath: specifiedSkillForeignWorkerPledgeFile,
        input: uploadFileData?.specifiedSkillForeignWorkerPledgeInput,
      },

      // 登録法人構成名簿を印刷したもの
      {
        order: 49.2,
        key: "registeredCorporateStructureListPrintedFilePath",
        filePath: registeredCorporateStructureListPrintedFile,
      },

      // 建設特定技能受入計画の認定証の写し
      {
        order: 49.3,
        key: "constructionSpecifiedSkillAcceptancePlanCertFilePath",
        filePath: constructionSpecifiedSkillAcceptancePlanCertFile,
        input: uploadFileData?.constructionSpecifiedSkillAcceptancePlanCertInput,
      },

      // ②建築物環境衛生総合管理業登録証明書
      {
        order: 49,
        key: "buildingEnvHealthMgmtRegistrationCertFilePath",
        filePath: buildingEnvHealthMgmtRegistrationCertFile,
        input: uploadFileData?.buildingEnvHealthMgmtRegistrationCertInput,
      },

      // 協議会の構成員であることの証明書
      {
        order: 50,
        key: "councilMembershipCertFilePath",
        filePath: councilMembershipCertFile,
      },

      // 協議会の構成員であることの証明書
      {
        order: 52,
        key: "councilMembershipCertFilePath",
        filePath: councilMembershipCertFile,
      },

      // 造船・舶用工業事業者の確認通知書
      {
        order: 53,
        key: "shipbuildingIndustryConfirmationFilePath",
        filePath: shipbuildingIndustryConfirmationFile,
        input: uploadFileData?.shipbuildingIndustryConfirmationInput,
      },

      // 協議会の構成員であることの証明書
      {
        order: 54,
        key: "councilMembershipCertFilePath",
        filePath: councilMembershipCertFile,
      },

      // ①自動車整備分野特定技能協議会入会届出書兼構成員資格証明書（受付印があるもの）
      {
        order: 57,
        key: "specifiedSkillAutomobileMaintenanceCertFilePath",
        filePath: specifiedSkillAutomobileMaintenanceCertFile,
      },

      // ②自動車整備分野特定技能協議会構成員資格証明書発行申請書（受付印があるもの）
      {
        order: 58,
        key: "specifiedSkillAutomobileMaintenanceIssuranceFilePath",
        filePath: specifiedSkillAutomobileMaintenanceIssuranceFile,
      },

      // 協議会の構成員であることの証明書(特定技能所属機関)
      {
        order: 64,
        key: "councilMembershipCertFilePath",
        filePath: councilMembershipCertFile,
      },

      // 旅館業許可証(旅館・ホテル営業許可書)の写し
      {
        order: 67,
        key: "hotelBusinessLicenseFilePath",
        filePath: hotelBusinessLicenseFile,
        input: uploadFileData?.hotelBusinessLicenseInput,
      },

      // 協議会の構成員であることの証明書(特定技能所属機関)
      {
        order: 68,
        key: "councilMembershipCertFilePath",
        filePath: councilMembershipCertFile,
      },

      // 協議会の構成員であることの証明書(特定技能所属機関)
      {
        order: 71,
        key: "councilMembershipCertFilePath",
        filePath: councilMembershipCertFile,
      },

      // 旅館業法の旅館・ホテル営業の営業許可証の写し
      {
        key: "ryokanBusinessLicenseFilePath",
        order: 73.1,
        filePath: ryokanBusinessLicenseFile,
        input: uploadFileData?.ryokanBusinessLicenseInput,
      },

      // 風営法の風俗営業の営業許可証又の写し
      {
        key: "adultEntertainmentBusinessLicenseFilePath",
        order: 73.1,
        filePath: adultEntertainmentBusinessLicenseFile,
        input: uploadFileData?.adultEntertainmentBusinessLicenseInput,
      },

      // 保健所長の営業許可証又は届出書の写し
      {
        order: 74,
        key: "businessLicenseOrNotificationFormFilePath",
        filePath: businessLicenseOrNotificationFormFile,
        input: uploadFileData?.businessLicenseOrNotificationFormInput,
      },

      // 協議会の構成員であることの証明書
      {
        order: 75,
        key: "councilMembershipCertFilePath",
        filePath: councilMembershipCertFile,
      },
    ];

    const table3And2Files: TMergeRenewVisaFile[] = [
      // 特定技能外国人の在留諸申請に係る提出書類一覧表(第3表の2)
      // order: 78,

      // 協議会の構成員であることの証明書(特定技能所属機関)
      {
        order: 79,
        key: "councilMembershipCertFilePath",
        filePath: councilMembershipCertFile,
      },
    ];

    const table3And3Files: TMergeRenewVisaFile[] = [
      // 特定技能外国人の在留諸申請に係る提出書類一覧表(第3表の3)
      // order: 81,

      // ①許可証の写し
      {
        order: 82,
        key: "fishingLicenseCopy1FilePath",
        filePath: fishingLicenseCopy1File,
        input: uploadFileData?.fishingLicenseCopy1Input,
      },

      // ②免許の指令書の写し
      {
        order: 83,
        key: "fishingLicenseCopy2FilePath",
        filePath: fishingLicenseCopy2File,
        input: uploadFileData?.fishingLicenseCopy2Input,
      },

      // ③その他許可または免許を受け漁業又は養殖業を営んでいることが確認できる公的な書類の写し
      {
        order: 84,
        key: "fishingLicenseCopy3FilePath",
        filePath: fishingLicenseCopy3File,
        input: uploadFileData?.fishingLicenseCopy3Input,
      },

      // ①当該組合の漁業権の内容たる漁業又は養殖業を営むことを確認できる当該組合が発行した書類の写し
      {
        order: 85,
        key: "fishingCooperativeRightsDocumentFilePath",
        filePath: fishingCooperativeRightsDocumentFile,
        input: uploadFileData?.fishingCooperativeRightsDocumentInput,
      },

      // ②その他当該組合に所属して漁業又は養殖業を営んでいることが確認できる書類の写し
      {
        order: 86,
        key: "fishingCooperativeMembershipDocumentFilePath",
        filePath: fishingCooperativeMembershipDocumentFile,
        input: uploadFileData?.fishingCooperativeMembershipDocumentInput,
      },

      // ①漁船原簿謄本の写し
      {
        order: 87,
        key: "fishingBoatRegistryCopyFilePath",
        filePath: fishingBoatRegistryCopyFile,
        input: uploadFileData?.fishingBoatRegistryCopyInput,
      },

      // ②漁船登録票の写し
      {
        order: 88,
        key: "fishingBoatRegistrationCertCopyFilePath",
        filePath: fishingBoatRegistrationCertCopyFile,
        input: uploadFileData?.fishingBoatRegistrationCertCopyInput,
      },

      // 協議会の構成員であることの証明書(特定技能所属機関)
      {
        order: 89,
        key: "councilMembershipCertFilePath",
        filePath: councilMembershipCertFile,
      },
    ];

    const { yesCount: table1YesCount, inputCount: table1InputCount } = handleCheckFileListTable(renewVisaEmployeeApplication, "table_1", table1Files);
    const { yesCount: table2And1YesCount, inputCount: table2And1InputCount } = handleCheckFileListTable(
      renewVisaEmployeeApplication,
      "table_2_1",
      table2And1Files,
    );
    const { yesCount: table2And2YesCount, inputCount: table2And2InputCount } = handleCheckFileListTable(
      renewVisaEmployeeApplication,
      "table_2_2",
      table2And2Files,
    );
    const { yesCount: table2And3YesCount, inputCount: table2And3InputCount } = handleCheckFileListTable(
      renewVisaEmployeeApplication,
      "table_2_3",
      table2And3Files,
    );
    const { yesCount: table3And1YesCount, inputCount: table3And1InputCount } = handleCheckFileListTable(
      renewVisaEmployeeApplication,
      "table_3_1",
      table3And1Files,
    );
    const { yesCount: table3And2YesCount, inputCount: table3And2InputCount } = handleCheckFileListTable(
      renewVisaEmployeeApplication,
      "table_3_2",
      table3And2Files,
    );
    const { yesCount: table3And3YesCount, inputCount: table3And3InputCount } = handleCheckFileListTable(
      renewVisaEmployeeApplication,
      "table_3_3",
      table3And3Files,
    );

    const isNotSelectOneOfAToE = basicInfoData?.conditions.length === 0 || basicInfoData?.conditions.includes(COMPANY_CONDITION.NONE_A_TO_E);

    return {
      table1Files,
      table2And1Files,
      table2And2Files,
      table2And3Files,
      table3And1Files,
      table3And2Files,
      table3And3Files,
      isShowTable1: table1YesCount > 0 || table1InputCount > 0,
      isShowTable2And1: table2And1YesCount > 0 || table2And1InputCount > 0,
      isShowTable2And2:
        isNotSelectOneOfAToE && company?.division === COMPANY_DIVISION.CORPORATE && (table2And2YesCount > 0 || table2And2InputCount > 0),
      isShowTable2And3:
        isNotSelectOneOfAToE && company?.division === COMPANY_DIVISION.SOLE_PROPRIETOR && (table2And3YesCount > 0 || table2And3InputCount > 0),
      isShowTable3And1: table3And1YesCount > 0 || table3And1InputCount > 0,
      isShowTable3And2: table3And2YesCount > 0 || table3And2InputCount > 0,
      isShowTable3And3: table3And3YesCount > 0 || table3And3InputCount > 0,
    };
  };

  const handleCreateCoverPagePdf = async (renewVisaEmployeeApplication: TRenewVisaEmployeeApplicationDocument) => {
    try {
      if (!renewVisaCoverPageArrayBuffer) return;
      const bytes = new Uint8Array(renewVisaCoverPageArrayBuffer);
      const templatePdf = await PDFDocument.load(bytes, { ignoreEncryption: true });
      const editedPdf = await PDFDocument.create();

      // Copy pages from raw pdf into final pdf
      const copiedPages = await editedPdf.copyPages(templatePdf, templatePdf.getPageIndices());
      copiedPages.forEach((page) => editedPdf.addPage(page));

      editedPdf.registerFontkit(fontkit);

      if (!fontBytes) return;
      const customFont = await editedPdf.embedFont(fontBytes);
      const page = editedPdf.getPage(0);

      // Add employee full name
      const employeeFullName = getFullName({
        firstName: renewVisaEmployeeApplication.employee?.firstName || "",
        middleName: renewVisaEmployeeApplication.employee?.middleName,
        lastName: renewVisaEmployeeApplication.employee?.lastName,
      });
      page.drawText(employeeFullName, {
        x: 300,
        y: 115,
        size: 10,
        font: customFont,
      });

      // Add company name
      page.drawText(renewVisaDocumentGroup?.company?.companyName || "", {
        x: 300,
        y: 84.5,
        size: 10,
        font: customFont,
      });

      const pdfBytes = await editedPdf.save();
      const blob = new Blob([pdfBytes as BlobPart], { type: "application/pdf" });
      const file = new File([blob], `cover.pdf`, { type: blob.type });
      return file;
    } catch (error) {
      toast.error(API_UNKNOWN_ERROR_MESSAGE);
      console.log(error);
    }
  };

  const handleCheckFileListTable = (
    renewVisaEmployeeApplication: TRenewVisaEmployeeApplicationDocument | undefined,
    tableKey: "table_1" | "table_2_1" | "table_2_2" | "table_2_3" | "table_3_1" | "table_3_2" | "table_3_3",
    files: TMergeRenewVisaFile[],
  ) => {
    files = files.sort((a, b) => a.order - b.order);
    let yesCount = 0;
    let noCount = 0;
    let inputCount = 0;
    let templatePdfUrl: string | undefined = "";
    let fileFields: TCheckFileField[] = [];
    let divisions: string[] | undefined = undefined;

    const companySpecificIndustryName =
      renewVisaEmployeeApplication?.contractSpecificIndustry?.companySpecificIndustry.companySpecificIndustry ||
      renewVisaEmployeeApplication?.employee.contractSpecificBusinessClassificationOne?.companySpecificBusinessClassification.companySpecificIndustry
        .companySpecificIndustry;

    const companyDivision = company?.division;

    const residenceStatusId =
      toNumber(renewVisaEmployeeApplication?.employeeInfoTwoResidenceStatusId) || toNumber(renewVisaEmployeeApplication?.employee.residenceStatusId);

    if (residenceStatusId === RESIDENCE_STATUS.SPECIFIED_SKILL_NO1) {
      switch (tableKey) {
        case "table_1":
          templatePdfUrl = "/pdf/renew_visa_table_1_skill_1.pdf";
          divisions = [COMPANY_DIVISION.CORPORATE, COMPANY_DIVISION.SOLE_PROPRIETOR];
          fileFields = [
            {
              key: "applicantListFilePath", // 申請人名簿
              page: 1,
              yes: { x: 415, y: 735.5, size: 6 },
              no: { x: 429.5, y: 735.5, size: 6 },
            },
            {
              key: "unknown", // 特定技能外国人の在留諸申請に係る提出書類一覧表(本表)
              page: 1,
              yes: { x: 415, y: 692, size: 6 },
              no: { x: 429.5, y: 692, size: 6 },
              fixedValue: "yes",
              // inputArea: { x: 450, y: 750, width: 200, height: 200 },
            },
            {
              key: "unknown", // 在留期間更新許可申請書
              page: 1,
              yes: { x: 415, y: 647.5, size: 6 },
              no: { x: 429.5, y: 647.5, size: 6 },
              fixedValue: "no",
            },
            {
              key: "employmentContractCopyFilePath", // 特定技能雇用契約書の写し
              page: 1,
              yes: { x: 415, y: 610.5, size: 6 },
              no: { x: 429.5, y: 610.5, size: 6 },
            },
            {
              key: "employmentConditionsCopyFilePaths", // 雇用条件書の写し
              page: 1,
              yes: { x: 415, y: 579.5, size: 6 },
              no: { x: 429.5, y: 579.5, size: 6 },
            },
            {
              key: "wagePaymentFilePath", // 賃金の支払
              page: 1,
              yes: { x: 415, y: 546, size: 6 },
              no: { x: 429.5, y: 546, size: 6 },
            },
            {
              key: "annualCalendarWithLanguageFilePath", // 申請人が十分に理解できる言語が併記された年間カレンダーの写し
              page: 1,
              yes: { x: 415, y: 513, size: 6 },
              no: { x: 429.5, y: 513, size: 6 },
            },
            {
              key: "annualVariableWorkAgreementFilePath", // １年単位の変形労働時間制に関する協定書の写し
              page: 1,
              yes: { x: 415, y: 479, size: 6 },
              no: { x: 429.5, y: 479, size: 6 },
            },
            {
              key: "applicantResidentTaxPaymentCertFilePath", // 全ての納期が経過している直近1年度分の個人住民税の納税証明書
              page: 1,
              yes: { x: 415, y: 394.5, size: 6 },
              no: { x: 429.5, y: 394.5, size: 6 },
              inputArea: { x: 437, y: 326.5, width: 82, height: 135 },
            },
            {
              key: "applicantIndividualResidentTaxCertFilePath", // 課税年度が納税証明書の賦課年度と同一年度の申請人の個人住民税の課税証明書
              page: 1,
              yes: { x: 415, y: 304.5, size: 6 },
              no: { x: 429.5, y: 304.5, size: 6 },
              inputArea: { x: 437, y: 282, width: 82, height: 44 },
            },
            {
              key: "applicantWithholdingSlipCopyFilePath", // 課税証明書と同一年の申請人の給与所得の源泉徴収票の写し
              page: 1,
              yes: { x: 415, y: 176, size: 6 },
              no: { x: 429.5, y: 176, size: 6 },
              inputArea: { x: 437, y: 70, width: 82, height: 212 },
            },

            // Page 2
            {
              key: "applicantHealthInsuranceCardCopyFilePath", // 申請人の国民健康保険被保険者証の写し
              page: 2,
              yes: { x: 415, y: 691.5, size: 6 },
              no: { x: 429.5, y: 691.5, size: 6 },
              inputArea: { x: 437, y: 626, width: 81.5, height: 130 },
            },
            {
              key: "applicantHealthInsurancePaymentCertFilePath", // 直近1年度分の申請人の国民健康保険料(税)納 付証明書
              page: 2,
              yes: { x: 415, y: 548.5, size: 6 },
              no: { x: 429.5, y: 548.5, size: 6 },
              inputArea: { x: 437, y: 469, width: 81.5, height: 157 },
            },
            {
              key: "applicantHealthInsuranceRecordInquiryResponseFilePath", // 申請人の被保険者記録照会回答票
              page: 2,
              yes: { x: 415, y: 390.5, size: 6 },
              no: { x: 429.5, y: 390.5, size: 6 },
              inputArea: { x: 437, y: 311, width: 81.5, height: 158.5 },
            },
            {
              key: "applicantPensionReceiptCopy1FilePath",
              page: 2,
              yes: { x: 415, y: 238, size: 6 },
              no: { x: 429.5, y: 238, size: 6 },
              inputArea: { x: 437, y: 164, width: 81.5, height: 146.5 },
              children: [
                "applicantPensionRecordInquiry2FilePath", //①申請人の被保険者記録照会(納付Ⅱ)
                "applicantPensionReceiptCopy1FilePath", // ②申請人の国民年金保険料領収証書の写し
              ],
            },
            {
              key: "previousApplicationDocumentsFilePath", // 前回申請時に履行すべきであった公的義務に係る書類
              page: 2,
              yes: { x: 415, y: 114, size: 6 },
              no: { x: 429.5, y: 114, size: 6 },
            },
            {
              key: "publicDutyPledgeFilePath", // 公的義務履行に関する誓約書
              page: 2,
              yes: { x: 415, y: 52.5, size: 6 },
              no: { x: 429.5, y: 52.5, size: 6 },
            },
          ];
          break;
        case "table_2_1":
          templatePdfUrl = "/pdf/renew_visa_table_2_1_skill_1.pdf";
          divisions = [COMPANY_DIVISION.CORPORATE, COMPANY_DIVISION.SOLE_PROPRIETOR];
          fileFields = [
            {
              key: "quarterlyReportCopyFilePath", //四季報の写し又は日本の証券取引所に上場していることを証明する文書（写し）
              page: 1,
              yes: { x: 436.5, y: 707, size: 6 },
              no: { x: 454.5, y: 707, size: 6 },
            },
            {
              key: "innovationCompanyProofFilePath", //高度専門職省令第１条第１項各
              page: 1,
              yes: { x: 436.5, y: 642, size: 6 },
              no: { x: 454.5, y: 642, size: 6 },
            },
            {
              // key: "innovationCompanyProofFilePath", //高度専門職省令第１条第１項各
              key: "certifiedCompanyProofFilePath", //「一定の条件を満たす企業等
              page: 1,
              yes: { x: 436.5, y: 564.5, size: 6 },
              no: { x: 454.5, y: 564.5, size: 6 },
            },
            {
              key: "annualSalaryReportCopyFilePath", // 前年分の職員の給与所得の源泉
              page: 1,
              yes: { x: 436.5, y: 478.5, size: 6 },
              no: { x: 454.5, y: 478.5, size: 6 },
            },
            {
              key: "immigrationElectronicSystemPledgeFilePath", //出入国在留管理庁電子届出シス
              page: 1,
              yes: { x: 436.5, y: 381.5, size: 6 },
              no: { x: 454.5, y: 381.5, size: 6 },
            },
            {
              key: "documentOmissionPledgeFilePath", //書類の省略に当たっての誓約書
              page: 1,
              yes: { x: 436.5, y: 307, size: 6 },
              no: { x: 454.5, y: 307, size: 6 },
            },
          ];
          break;
        case "table_2_2":
          templatePdfUrl = "/pdf/renew_visa_table_2_2_skill_1.pdf";
          divisions = [COMPANY_DIVISION.CORPORATE];
          fileFields = [
            // {
            //   key: "specificSkillsInstitutionOverviewDocumentFilePath", // 特定技能所属機関概要書
            //   page: 1,
            //   yes: { x: 436.5, y: 713.5, size: 6 },
            //   no: { x: 454, y: 713.5, size: 6 },
            //   inputArea: { x: 464, y: 671, width: 69, height: 83 },
            // },
            // {
            //   key: "registrationCertFilePath", // 登記事項証明書
            //   page: 1,
            //   yes: { x: 436.5, y: 644.5, size: 6 },
            //   no: { x: 454, y: 644.5, size: 6 },
            //   inputArea: { x: 464, y: 616, width: 69, height: 55 },
            // },
            // {
            //   key: "executiveResidentRecordCopyFilePath", // 業務執行に関与する役員の住民票の写し
            //   page: 1,
            //   yes: { x: 436.5, y: 589, size: 6 },
            //   no: { x: 454, y: 589, size: 6 },
            //   inputArea: { x: 464, y: 561, width: 69, height: 54 },
            // },
            // {
            //   key: "specificSkillsInstitutionOfficerAffidavitFilePath", // 特定技能所属機関の役員に関する誓約書
            //   page: 1,
            //   yes: { x: 436.5, y: 533, size: 6 },
            //   no: { x: 454, y: 533, size: 6 },
            //   inputArea: { x: 464, y: 504, width: 69, height: 58 },
            // },
            {
              key: "laborInsuranceNoDelegationFilePath", // 労働保険事務組合に事務委託していない場合
              page: 1,
              yes: { x: 436.5, y: 448, size: 6 },
              no: { x: 454, y: 448, size: 6 },
              inputArea: { x: 464, y: 410, width: 69, height: 74 },
            },
            {
              key: "laborInsuranceDelegationFilePath", // 労働保険事務組合に事務委託している場合
              page: 1,
              yes: { x: 436.5, y: 372, size: 6 },
              no: { x: 454, y: 372, size: 6 },
              inputArea: { x: 464, y: 336, width: 69, height: 74 },
            },
            {
              key: "socialInsurancePaymentStatusCertFilePath", // 社会保険料納入状況回答票又は健康保険・厚生年金保険料領収証書の写し
              page: 1,
              yes: { x: 436.5, y: 302, size: 6 },
              no: { x: 454, y: 302, size: 6 },
              inputArea: { x: 464, y: 268, width: 69, height: 69 },
            },
            {
              key: "taxOfficePaymentCertNo3FilePath", // 税務署発行の納税証明書(その３)
              page: 1,
              yes: { x: 436.5, y: 235.5, size: 6 },
              no: { x: 454, y: 235.5, size: 6 },
              inputArea: { x: 464, y: 203, width: 69, height: 66 },
            },
            {
              key: "corporateResidentTaxPaymentCertFilePath", // 法人住民税の市町村発行の納税証明書
              page: 1,
              yes: { x: 436.5, y: 171, size: 6 },
              no: { x: 454, y: 171, size: 6 },
              inputArea: { x: 464, y: 139, width: 69, height: 65 },
            },
            {
              key: "publicDutyComplianceFilePath", // 公的義務履行に関する説明書
              page: 1,
              yes: { x: 436.5, y: 118, size: 6 },
              no: { x: 454, y: 118, size: 6 },
              inputArea: { x: 464, y: 100, width: 69, height: 39 },
            },
          ];
          break;
        case "table_2_3":
          templatePdfUrl = "/pdf/renew_visa_table_2_3_skill_1.pdf";
          divisions = [COMPANY_DIVISION.SOLE_PROPRIETOR];
          fileFields = [
            // {
            //   key: "specificSkillsInstitutionOverviewDocumentFilePath", // 特定技能所属機関概要書
            //   page: 1,
            //   yes: { x: 436.5, y: 711, size: 6 },
            //   no: { x: 454, y: 711, size: 6 },
            //   inputArea: { x: 464, y: 668, width: 69, height: 86 },
            // },
            // {
            //   key: "individualBusinessOwnerResidentCertFilePath", // 個人事業主の住民票の写し
            //   page: 1,
            //   yes: { x: 436.5, y: 640.5, size: 6 },
            //   no: { x: 454, y: 640.5, size: 6 },
            //   inputArea: { x: 464, y: 613, width: 69, height: 54 },
            // },
            {
              key: "laborInsuranceNoDelegationFilePath", // 労働保険事務組合に事務委託していない場合
              page: 1,
              yes: { x: 436.5, y: 560, size: 6 },
              no: { x: 454, y: 560, size: 6 },
              inputArea: { x: 464, y: 521.5, width: 69, height: 75 },
            },
            {
              key: "laborInsuranceDelegationFilePath", // 労働保険事務組合に事務委託している場合
              page: 1,
              yes: { x: 436.5, y: 484, size: 6 },
              no: { x: 454, y: 484, size: 6 },
              inputArea: { x: 464, y: 445, width: 69, height: 75 },
            },
            {
              key: "healthInsuranceWelfarePensionCertFilePath", // 健康保険・厚生年金保険の適用事業所の場合
              page: 1,
              yes: { x: 436.5, y: 391.5, size: 6 },
              no: { x: 454, y: 391.5, size: 6 },
              inputArea: { x: 464, y: 353.5, width: 69, height: 75 },
            },
            {
              key: "nonHealthInsuranceWelfarePensionCertFilePath", // 健康保険・厚生年金保険の適用事業所でない場合
              page: 1,
              yes: { x: 436.5, y: 320, size: 6 },
              no: { x: 454, y: 320, size: 6 },
              inputArea: { x: 464, y: 285, width: 69, height: 69 },
            },
            {
              key: "individualHealthInsurancePaymentCertFilePath", // 個人事業主の国民健康保険料(税)納付証明書
              page: 1,
              yes: { x: 436.5, y: 233.5, size: 6 },
              no: { x: 454, y: 233.5, size: 6 },
              inputArea: { x: 464, y: 182, width: 69, height: 103 },
            },
            {
              key: "individualPensionReceiptCopyFilePath", // 個人事業主の国民年金保険料領収証書の写し又は被保険者記録照会(納付Ⅱ)
              page: 1,
              yes: { x: 436.5, y: 128.5, size: 6 },
              no: { x: 454, y: 128.5, size: 6 },
              inputArea: { x: 464, y: 74, width: 69, height: 106 },
            },
            {
              key: "individualBusinessOwnerTaxOfficePaymentCertNo3FilePath", // 個人事業主の税務署発行の納税証明書(その３)
              page: 2,
              yes: { x: 436.5, y: 720, size: 6 },
              no: { x: 454, y: 720, size: 6 },
              inputArea: { x: 464, y: 687, width: 69, height: 67 },
            },
            {
              key: "individualResidentTaxCertFilePath", // 個人事業主の個人住民税の市町村発行の納税証明書
              page: 2,
              yes: { x: 436.5, y: 654, size: 6 },
              no: { x: 454, y: 654, size: 6 },
              inputArea: { x: 464, y: 621.5, width: 69, height: 65 },
            },
            {
              key: "publicDutyComplianceFilePath", // 公的義務履行に関する説明書
              page: 2,
              yes: { x: 436.5, y: 599, size: 6 },
              no: { x: 454, y: 599, size: 6 },
              inputArea: { x: 464, y: 576, width: 69, height: 45 },
            },
          ];
          break;
        case "table_3_1":
          templatePdfUrl = "/pdf/renew_visa_table_3_1_skill_1.pdf";
          divisions = [COMPANY_DIVISION.CORPORATE, COMPANY_DIVISION.SOLE_PROPRIETOR];
          fileFields = [
            {
              key: "careWorkplaceOverviewFilePath", // 介護分野における業務を行わせる事業所の概要書
              page: 1,
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.NURSING_CARE, // 介護
              yes: { x: 416.5, y: 746, size: 6 },
              no: { x: 431.5, y: 746, size: 6 },
            },
            {
              key: "councilMembershipCertFilePath", // 協議会の構成員であることの証明書
              page: 1,
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.NURSING_CARE, // 介護
              yes: { x: 416.5, y: 723, size: 6 },
              no: { x: 431.5, y: 723, size: 6 },
            },
            {
              key: "specificSkillOrganizationOverviewFilePath",
              page: 1,
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.BUILDING_CLEANING, // ビルクリーニング
              yes: { x: 416.5, y: 650.5, size: 6 },
              no: { x: 431.5, y: 650.5, size: 6 },
              inputArea: { x: 439, y: 589, width: 81.5, height: 123 },
              children: [
                "specificSkillOrganizationOverviewFilePath", //  ①建築物清掃業登録証明書
                "buildingEnvHealthMgmtRegistrationCertFilePath", // ②建築物環境衛生総合管理業登録証明書
              ],
            },
            {
              key: "councilMembershipCertFilePath", // 協議会の構成員であることの証明書
              page: 1,
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.BUILDING_CLEANING, // ビルクリーニング
              yes: { x: 416.5, y: 578, size: 6 },
              no: { x: 431.5, y: 578, size: 6 },
            },
            {
              key: "specifiedSkillForeignWorkerPledgeFilePath", // （参考様式3-1）特定技能外国人の受入れに関する誓約書
              page: 1,
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.INDUSTRIAL_PRODUCT_MANUFACTURING, //  工業製品製造
              yes: { x: 416.5, y: 522.5, size: 6 },
              no: { x: 431.5, y: 522.5, size: 6 },
              inputArea: { x: 439, y: 477.5, width: 81.5, height: 88.5 },
            },
            {
              key: "registeredCorporateStructureListPrintedFilePath", //登録法人のホームページに掲載されている登録法人構成員名簿を印刷したもの（特定技能所属機関及び申請人の就業場所の名称が掲載されているもの）
              page: 1,
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.INDUSTRIAL_PRODUCT_MANUFACTURING, //  工業製品製造
              yes: { x: 416.5, y: 427, size: 6 },
              no: { x: 431.5, y: 427, size: 6 },
            },
            {
              key: "constructionSpecifiedSkillAcceptancePlanCertFilePath", // 建設特定技能受入計画の認定証の写し
              page: 1,
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.CONSTRUCTION, //  建設
              yes: { x: 416.5, y: 325, size: 6 },
              no: { x: 431.5, y: 325, size: 6 },
              inputArea: { x: 439, y: 273, width: 81.5, height: 103 },
            },
            {
              key: "shipbuildingIndustryConfirmationFilePath", // 造船・舶用工業事業者の確認通知書
              page: 1,
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.SHIPBUILDING, // 造船・舶用工業
              yes: { x: 416.5, y: 245.5, size: 6 },
              no: { x: 431.5, y: 245.5, size: 6 },
              inputArea: { x: 439, y: 217, width: 81.5, height: 56 },
            },
            {
              key: "councilMembershipCertFilePath", // 協議会の構成員であることの証明書（特定技能所属機関）
              page: 1,
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.SHIPBUILDING, // 造船・舶用工業
              yes: { x: 416.5, y: 206.5, size: 6 },
              no: { x: 431.5, y: 206.5, size: 6 },
            },
            {
              key: "unknown", // 造船・舶用工業分野における特定技能外国人の受入れに関する誓約書(登録支援機関)
              page: 1,
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.SHIPBUILDING, // 造船・舶用工業
              yes: { x: 416.5, y: 156, size: 6 },
              no: { x: 431.5, y: 156, size: 6 },
              inputArea: { x: 439, y: 116.5, width: 81.5, height: 78 },
              fixedValue: "no",
            },
            {
              key: "unknown", // 協議会の構成員であることの証明書(登録支援機関)
              page: 1,
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.SHIPBUILDING, // 造船・舶用工業
              yes: { x: 416.5, y: 100, size: 6 },
              no: { x: 431.5, y: 100, size: 6 },
              fixedValue: "no",
            },

            // Page 2
            {
              key: "specifiedSkillAutomobileMaintenanceCertFilePath", // 登録支援機関の以下のいずれかの書類
              page: 2,
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AUTOMOBILE_MAINTENANCE, // 自動車整備
              yes: { x: 416.5, y: 722.5, size: 6 },
              no: { x: 431.5, y: 722.5, size: 6 },
              children: [
                "specifiedSkillAutomobileMaintenanceCertFilePath", //①自動車整備分野特定技能協議会入会届出書兼構成員資格証明書（受付印があるもの）
                "specifiedSkillAutomobileMaintenanceIssuranceFilePath", //②自動車整備分野特定技能協議会構成員資格証明書発行申請書（受付印があるもの）
              ],
            },
            {
              key: "unknown", // 自動車整備分野における特定技能外国人の受入れに関する誓約書(登録支援機関)
              page: 2,
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AUTOMOBILE_MAINTENANCE, // 自動車整備
              yes: { x: 416.5, y: 650, size: 6 },
              no: { x: 431.5, y: 650, size: 6 },
              inputArea: { x: 439, y: 610.5, width: 81.5, height: 78 },
              fixedValue: "no",
            },
            {
              key: "unknown", // ①自動車整備士技能検定１級又は２級の合格証の写し or ②実務経験証明書 (注)支援責任者、支援担当者などの外国人の支援を行う者
              page: 2,
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AUTOMOBILE_MAINTENANCE, // 自動車整備
              yes: { x: 416.5, y: 566, size: 6 },
              no: { x: 431.5, y: 566, size: 6 },
              inputArea: { x: 439, y: 520.5, width: 81.5, height: 89.5 },
              fixedValue: "no",
            },
            {
              key: "unknown", // 登録支援機関の以下のいずれかの書類
              page: 2,
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AUTOMOBILE_MAINTENANCE, // 自動車整備
              yes: { x: 416.5, y: 459.5, size: 6 },
              no: { x: 431.5, y: 459.5, size: 6 },
              inputArea: { x: 439, y: 397, width: 81.5, height: 123.5 },
              fixedValue: "no",
            },
            {
              key: "councilMembershipCertFilePath", // 協議会の構成員であることの証明書(特定技能所属機関)
              page: 2,
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AVIATION, // 航空
              yes: { x: 416.5, y: 386.5, size: 6 },
              no: { x: 431.5, y: 386.5, size: 6 },
            },
            {
              key: "unknown", // 航空分野における特定技能外国人の受入れに関する誓約書(登録支援機関)
              page: 2,
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AVIATION, // 航空
              yes: { x: 416.5, y: 336.5, size: 6 },
              no: { x: 431.5, y: 336.5, size: 6 },
              inputArea: { x: 439, y: 296.5, width: 81.5, height: 78 },
              fixedValue: "no",
            },
            {
              key: "unknown", // 協議会の構成員であることの証明書(登録支援機関)
              page: 2,
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AVIATION, // 航空
              yes: { x: 416.5, y: 280.5, size: 6 },
              no: { x: 431.5, y: 280.5, size: 6 },
              // inputArea: { x: 448.5, y: 650, width: 82, height: 29.5 },
              fixedValue: "no",
            },

            // Page 3
            {
              key: "hotelBusinessLicenseFilePath", // 旅館業許可証(旅館・ホテル営業許可書)の写し
              page: 3,
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.ACCOMMODATION, // 宿泊
              yes: { x: 416.5, y: 666.5, size: 6 },
              no: { x: 431.5, y: 666.5, size: 6 },
              inputArea: { x: 439, y: 577, width: 81.5, height: 178.5 },
            },
            {
              key: "councilMembershipCertFilePath", // 協議会の構成員であることの証明書(特定技能所属機関)
              page: 3,
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.ACCOMMODATION, // 宿泊
              yes: { x: 416.5, y: 566, size: 6 },
              no: { x: 431.5, y: 566, size: 6 },
            },
            {
              key: "unknown", // 宿泊分野における特定技能外国人の受入れに関する誓約書(登録支援機関)
              page: 3,
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.ACCOMMODATION, // 宿泊
              yes: { x: 416.5, y: 516, size: 6 },
              no: { x: 431.5, y: 516, size: 6 },
              inputArea: { x: 439, y: 476, width: 81.5, height: 77.5 },
              fixedValue: "no",
            },
            {
              key: "unknown", // 協議会の構成員であることの証明書(登録支援機関)
              page: 3,
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.ACCOMMODATION, // 宿泊
              yes: { x: 416.5, y: 461.5, size: 6 },
              no: { x: 431.5, y: 461.5, size: 6 },
              // inputArea: { x: 468, y: 511.5, width: 66, height: 30.5 },
              fixedValue: "no",
            },
            {
              key: "councilMembershipCertFilePath", // 協議会の構成員であることの証明書(特定技能所属機関)
              page: 3,
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AUTOMOBILE_TRANSPORTATION, //  自動車運送業
              yes: { x: 416.5, y: 435, size: 6 },
              no: { x: 431.5, y: 435, size: 6 },
            },
            {
              key: "unknown", // 自動車運送業分野における特定技能外国人の受入れに関する誓約書（登録支援機関）
              page: 3,
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AUTOMOBILE_TRANSPORTATION, //  自動車運送業
              yes: { x: 416.5, y: 384.5, size: 6 },
              no: { x: 431.5, y: 384.5, size: 6 },
              inputArea: { x: 439, y: 436.5, width: 81.5, height: 31 },
              fixedValue: "no",
            },
            {
              key: "unknown", // 協議会の構成員であることの証明書(登録支援機関)
              page: 3,
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AUTOMOBILE_TRANSPORTATION, //  自動車運送業
              yes: { x: 416.5, y: 330.5, size: 6 },
              no: { x: 431.5, y: 330.5, size: 6 },
              // inputArea: { x: 468, y: 407, width: 66, height: 30 },
              fixedValue: "no",
            },
            {
              key: "councilMembershipCertFilePath", // 協議会の構成員であることの証明書(特定技能所属機関)
              page: 3,
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.RAILWAY, //  鉄道
              yes: { x: 416.5, y: 303.5, size: 6 },
              no: { x: 431.5, y: 303.5, size: 6 },
            },
            {
              key: "unknown", // 鉄道分野における特定技能外国人の受入れに関する誓約書(登録支援機関)
              page: 3,
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.RAILWAY, // 鉄道
              yes: { x: 416.5, y: 253.5, size: 6 },
              no: { x: 431.5, y: 253.5, size: 6 },
              inputArea: { x: 439, y: 253.5 - 77 / 2, width: 81.5, height: 77 },
              fixedValue: "no",
            },
            {
              key: "unknown", // 協議会の構成員であることの証明書(登録支援機関)
              page: 3,
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.RAILWAY, // 鉄道
              yes: { x: 416.5, y: 197, size: 6 },
              no: { x: 431.5, y: 197, size: 6 },
              // inputArea: { x: 468, y: 407, width: 66, height: 30 },
              fixedValue: "no",
            },
            {
              key: "councilMembershipCertFilePath", // 協議会の構成員であることの証明書(特定技能所属機関)
              page: 3,
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FOOD_AND_BEVERAGE_MANUFACTURING, //  飲食料品製造業
              yes: { x: 416.5, y: 169.5, size: 6 },
              no: { x: 431.5, y: 169.5, size: 6 },
            },
            {
              key: "unknown", // 飲食料品製造業分野における特定技能外国人の受入れに関する誓約書(登録支援機関)
              page: 3,
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FOOD_AND_BEVERAGE_MANUFACTURING, //  飲食料品製造業
              yes: { x: 416.5, y: 119, size: 6 },
              no: { x: 431.5, y: 119, size: 6 },
              inputArea: { x: 439, y: 79.5, width: 81.5, height: 77.5 },
              fixedValue: "no",
            },
            {
              key: "unknown", // 協議会の構成員であることの証明書(登録支援機関)
              page: 3,
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FOOD_AND_BEVERAGE_MANUFACTURING, //  飲食料品製造業
              yes: { x: 416.5, y: 62.5, size: 6 },
              no: { x: 431.5, y: 62.5, size: 6 },
              // inputArea: { x: 468, y: 651.5, width: 66, height: 30 },
              fixedValue: "no",
            },
            // Page 4
            {
              key: "businessLicenseOrNotificationFormFilePath", // 保健所長の営業許可証又は届出書の写し
              page: 4,
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FOOD_SERVICE, //  外食業
              yes: { x: 416.5, y: 661, size: 6 },
              no: { x: 431.5, y: 661, size: 6 },
              inputArea: { x: 439, y: 566, width: 81.5, height: 190 },
            },
            {
              key: "ryokanBusinessLicenseFilePath", // 旅館業法の旅館・ホテル営業の営業許可証の写し
              page: 4,
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FOOD_SERVICE, //  外食業
              yes: { x: 416.5, y: 465.5, size: 6 },
              no: { x: 431.5, y: 465.5, size: 6 },
              inputArea: { x: 439, y: 364.5, width: 81.5, height: 201 },
            },
            {
              key: "adultEntertainmentBusinessLicenseFilePath", // 風営法の風俗営業の営業許可証又の写し
              page: 4,
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FOOD_SERVICE, //  外食業
              yes: { x: 416.5, y: 264, size: 6 },
              no: { x: 431.5, y: 264, size: 6 },
              inputArea: { x: 439, y: 163, width: 81.5, height: 201 },
            },
            {
              key: "councilMembershipCertFilePath", // 協議会の構成員であることの証明書(特定技能所属機関)
              page: 4,
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FOOD_SERVICE, //  外食業
              yes: { x: 416.5, y: 152, size: 6 },
              no: { x: 431.5, y: 152, size: 6 },
            },
            {
              key: "unknown", // 外食業分野における特定技能外国人の受入れに関する誓約書(登録支援機関)
              page: 4,
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FOOD_SERVICE, //  外食業
              yes: { x: 416.5, y: 102, size: 6 },
              no: { x: 431.5, y: 102, size: 6 },
              inputArea: { x: 439, y: 62, width: 81.5, height: 78.5 },
              fixedValue: "no",
            },
            {
              key: "unknown", // 協議会の構成員であることの証明書(登録支援機関)
              page: 4,
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FOOD_SERVICE, // 外食業
              yes: { x: 416.5, y: 46.5, size: 6 },
              no: { x: 431.5, y: 46.5, size: 6 },
              // inputArea: { x: 468, y: 406, width: 66, height: 30 },
              fixedValue: "no",
            },
            // Page 5
            {
              key: "councilMembershipCertFilePath", // 協議会の構成員であることの証明書
              page: 5,
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FORESTRY, // 林業
              yes: { x: 416.5, y: 744, size: 6 },
              no: { x: 431.5, y: 744, size: 6 },
            },
            {
              key: "councilMembershipCertFilePath", // 協議会の構成員であることの証明書
              page: 5,
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.WOOD_INDUSTRY, // 木材産業
              yes: { x: 416.5, y: 709, size: 6 },
              no: { x: 431.5, y: 709, size: 6 },
            },
          ];
          break;
        case "table_3_2":
          templatePdfUrl = "/pdf/renew_visa_table_3_2_skill_1.pdf";
          divisions = [COMPANY_DIVISION.CORPORATE, COMPANY_DIVISION.SOLE_PROPRIETOR];
          fileFields = [
            {
              key: "councilMembershipCertFilePath", // 協議会の構成員であることの証明書(特定技能所属機関)
              page: 1,
              yes: { x: 416.5, y: 745.5, size: 6 },
              no: { x: 431.5, y: 745.5, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 登録支援機関誓約書
              page: 1,
              yes: { x: 416.5, y: 693, size: 6 },
              no: { x: 431.5, y: 693, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
              inputArea: { x: 439, y: 653.5, width: 81.5, height: 78 },
              fixedValue: "no",
            },
            // Page 2
            {
              key: "unknown", // 農業又は農業に関連する業務を行っている場合
              page: 2,
              yes: { x: 416.5, y: 712.5, size: 6 },
              no: { x: 431.5, y: 712.5, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 地方公共団体等が出資（資本金の過半数）している機関である場合
              page: 2,
              yes: { x: 416.5, y: 623, size: 6 },
              no: { x: 431.5, y: 623, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 地方公共団体の職員等が役員として在籍している場合
              page: 2,
              yes: { x: 416.5, y: 539, size: 6 },
              no: { x: 431.5, y: 539, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 地方公共団体等が実質的に業務執行に関与している場合
              page: 2,
              yes: { x: 416.5, y: 455.5, size: 6 },
              no: { x: 431.5, y: 455.5, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 国家戦略特別区域法に規定する特定機関である場合
              page: 2,
              yes: { x: 416.5, y: 382.5, size: 6 },
              no: { x: 431.5, y: 382.5, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 農業分野において派遣形態で特定技能外国人の受入れ
              page: 2,
              yes: { x: 416.5, y: 327, size: 6 },
              no: { x: 431.5, y: 327, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 労働者派遣事業許可証の写し
              page: 2,
              yes: { x: 416.5, y: 270.5, size: 6 },
              no: { x: 431.5, y: 270.5, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 派遣計画書
              page: 2,
              yes: { x: 416.5, y: 231.5, size: 6 },
              no: { x: 431.5, y: 231.5, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 労働者派遣契約書の写し
              page: 2,
              yes: { x: 416.5, y: 209.5, size: 6 },
              no: { x: 431.5, y: 209.5, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 就業条件明示書の写し
              page: 2,
              yes: { x: 416.5, y: 187.5, size: 6 },
              no: { x: 431.5, y: 187.5, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 協議会の構成員であることの証明書(特定技能所属機関)
              page: 2,
              yes: { x: 416.5, y: 163.5, size: 6 },
              no: { x: 431.5, y: 163.5, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 登録支援機関誓約書
              page: 2,
              yes: { x: 416.5, y: 112.5, size: 6 },
              no: { x: 431.5, y: 112.5, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            // page 3
            {
              key: "unknown", // 派遣先の概要書（農業分野）
              page: 3,
              yes: { x: 416.5, y: 723, size: 6 },
              no: { x: 431.5, y: 723, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 派遣先事業者誓約書
              page: 3,
              yes: { x: 416.5, y: 684, size: 6 },
              no: { x: 431.5, y: 684, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 労働保険料等納付証明書(未納なし証明)
              page: 3,
              yes: { x: 416.5, y: 628.5, size: 6 },
              no: { x: 431.5, y: 628.5, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 労働保険概算・増加概算・確定
              page: 3,
              yes: { x: 416.5, y: 562.5, size: 6 },
              no: { x: 431.5, y: 562.5, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 労働保険事務組合が発行した直
              page: 3,
              yes: { x: 416.5, y: 483.5, size: 6 },
              no: { x: 431.5, y: 483.5, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 社会保険料納入状況回答票又は健康保険・厚生年金保
              page: 3,
              yes: { x: 416.5, y: 361, size: 6 },
              no: { x: 431.5, y: 361, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 税務署発行の納税証明書(その３)
              page: 3,
              yes: { x: 416.5, y: 187.5, size: 6 },
              no: { x: 431.5, y: 187.5, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            // Page 4
            {
              key: "unknown", // 法人住民税の市町村発行の納税証明書
              page: 4,
              yes: { x: 416.5, y: 672.5, size: 6 },
              no: { x: 431.5, y: 672.5, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 公的義務履行に関する説明書
              page: 4,
              yes: { x: 416.5, y: 571.5, size: 6 },
              no: { x: 431.5, y: 571.5, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            // Page 5
            {
              key: "unknown", // 農業又は農業に関連する業務を行っている場合
              page: 5,
              yes: { x: 416.5, y: 718.5, size: 6 },
              no: { x: 431.5, y: 718.5, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 地方公共団体等が出資（資本金の過半数）している機関である場合
              page: 5,
              yes: { x: 416.5, y: 640, size: 6 },
              no: { x: 431.5, y: 640, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 地方公共団体の職員等が役員として在籍している場合
              page: 5,
              yes: { x: 416.5, y: 562, size: 6 },
              no: { x: 431.5, y: 562, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 地方公共団体等が実質的に業務執行に関与している場合
              page: 5,
              yes: { x: 416.5, y: 483, size: 6 },
              no: { x: 431.5, y: 483, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 国家戦略特別区域法に規定する特定機関である場合
              page: 5,
              yes: { x: 416.5, y: 416, size: 6 },
              no: { x: 431.5, y: 416, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 農業分野において派遣形態で特定技能外国人の受入れ
              page: 5,
              yes: { x: 416.5, y: 360.5, size: 6 },
              no: { x: 431.5, y: 360.5, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 労働者派遣事業許可証の写し
              page: 5,
              yes: { x: 416.5, y: 304, size: 6 },
              no: { x: 431.5, y: 304, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 派遣計画書
              page: 5,
              yes: { x: 416.5, y: 265.5, size: 6 },
              no: { x: 431.5, y: 265.5, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 労働者派遣契約書の写し
              page: 5,
              yes: { x: 416.5, y: 242.5, size: 6 },
              no: { x: 431.5, y: 242.5, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 就業条件明示書の写し
              page: 5,
              yes: { x: 416.5, y: 220.5, size: 6 },
              no: { x: 431.5, y: 220.5, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 協議会の構成員であることの証明書(特定技能所属機関)
              page: 5,
              yes: { x: 416.5, y: 197.5, size: 6 },
              no: { x: 431.5, y: 197.5, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 登録支援機関誓約書
              page: 5,
              yes: { x: 416.5, y: 145.5, size: 6 },
              no: { x: 431.5, y: 145.5, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            // Page 6
            {
              key: "unknown", // 派遣先の概要書（農業分野）
              page: 6,
              yes: { x: 416.5, y: 722.5, size: 6 },
              no: { x: 431.5, y: 722.5, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 派遣先事業者誓約書
              page: 6,
              yes: { x: 416.5, y: 683.5, size: 6 },
              no: { x: 431.5, y: 683.5, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 労働保険料等納付証明書(未納なし証明)
              page: 6,
              yes: { x: 416.5, y: 627.5, size: 6 },
              no: { x: 431.5, y: 627.5, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 以下のいずれかの書類
              page: 6,
              yes: { x: 416.5, y: 566.5, size: 6 },
              no: { x: 431.5, y: 566.5, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 上記に対応する以下のいずれかの書類
              page: 6,
              yes: { x: 416.5, y: 493.5, size: 6 },
              no: { x: 431.5, y: 493.5, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // （該当する場合はチェック）
              page: 6,
              yes: { x: 416.5, y: 438, size: 6 },
              no: { x: 431.5, y: 438, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            // Page 7
            {
              key: "unknown", // 以下のいずれかの書類
              page: 7,
              yes: { x: 416.5, y: 672.5, size: 6 },
              no: { x: 431.5, y: 672.5, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 個人事業主の国民健康保険被保
              page: 7,
              yes: { x: 416.5, y: 538.5, size: 6 },
              no: { x: 431.5, y: 538.5, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 個人事業主の国民健康保険料
              page: 7,
              yes: { x: 416.5, y: 393, size: 6 },
              no: { x: 431.5, y: 393, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 個人事業主の国民年金保険料領
              page: 7,
              yes: { x: 416.5, y: 231, size: 6 },
              no: { x: 431.5, y: 231, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 個人事業主の税務署発行の納税証明書
              page: 7,
              yes: { x: 416.5, y: 102, size: 6 },
              no: { x: 431.5, y: 102, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            // Page 8
            {
              key: "unknown", // 個人事業主の納税証明書（その３）
              page: 8,
              yes: { x: 416.5, y: 650, size: 6 },
              no: { x: 431.5, y: 650, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 個人事業主の個人住民税の納税証明書
              page: 8,
              yes: { x: 416.5, y: 460, size: 6 },
              no: { x: 431.5, y: 460, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 公的義務履行に関する説明書
              page: 8,
              yes: { x: 416.5, y: 359, size: 6 },
              no: { x: 431.5, y: 359, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
          ];
          break;
        case "table_3_3":
          templatePdfUrl = "/pdf/renew_visa_table_3_3_skill_1.pdf";
          divisions = [COMPANY_DIVISION.CORPORATE, COMPANY_DIVISION.SOLE_PROPRIETOR];
          fileFields = [
            {
              key: "fishingLicenseCopy1FilePath",
              page: 1,
              yes: { x: 416.5, y: 704, size: 6 },
              no: { x: 431.5, y: 704, size: 6 },
              inputArea: { x: 438.5, y: 650.5, width: 82, height: 105 },
              children: [
                "fishingLicenseCopy1FilePath", // ①許可証の写し
                "fishingLicenseCopy2FilePath", // ②免許の指令書の写し
                "fishingLicenseCopy3FilePath", // ③その他許可または免許を受け
              ],
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FISHERY, //  漁業
            },
            {
              key: "fishingCooperativeRightsDocumentFilePath",
              page: 1,
              yes: { x: 416.5, y: 591, size: 6 },
              no: { x: 431.5, y: 591, size: 6 },
              inputArea: { x: 438.5, y: 530, width: 82, height: 120 },
              children: [
                "fishingCooperativeRightsDocumentFilePath", // ①当該組合の漁業権の内容たる漁業又は養殖業を営むことを確認できる当該組合が発行した書類の写し
                "fishingCooperativeMembershipDocumentFilePath", // ②その他当該組合に所属して漁業又は養殖業を営んでいることが確認できる書類の写し
              ],
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FISHERY, //  漁業
            },
            {
              key: "fishingBoatRegistryCopyFilePath",
              page: 1,
              yes: { x: 416.5, y: 491.5, size: 6 },
              no: { x: 431.5, y: 491.5, size: 6 },
              inputArea: { x: 438.5, y: 452, width: 82, height: 78 },
              children: [
                "fishingBoatRegistryCopyFilePath", // ①漁船原簿謄本の写し
                "fishingBoatRegistrationCertCopyFilePath", // ②漁船登録票の写し
              ],
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FISHERY, //  漁業
            },
            {
              key: "councilMembershipCertFilePath", // 協議会の構成員であることの証明書(特定技能所属機関)
              page: 1,
              yes: { x: 416.5, y: 441.5, size: 6 },
              no: { x: 431.5, y: 441.5, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FISHERY, //  漁業
            },
            {
              key: "unknown", // 漁業分野における特定技能外国人の受入れに関する誓約書(登録支援機関)
              page: 1,
              yes: { x: 416.5, y: 391, size: 6 },
              no: { x: 431.5, y: 391, size: 6 },
              inputArea: { x: 438.5, y: 351, width: 82, height: 79 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FISHERY, //  漁業
              fixedValue: "no",
            },
            // Page 2
            {
              key: "unknown", // ①所属機関が許可又は免許を受けて漁業若しくは養殖業を営んでいる場合
              page: 2,
              yes: { x: 416.5, y: 704, size: 6 },
              no: { x: 431.5, y: 704, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FISHERY, //  漁業
            },
            {
              key: "unknown", // ②所属機関が漁業協同組合に所属して漁業又は養殖業を営んでいる場合
              page: 2,
              yes: { x: 416.5, y: 598.5, size: 6 },
              no: { x: 431.5, y: 598.5, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FISHERY, //  漁業
            },
            {
              key: "unknown", // 以下のいずれかの書類
              page: 2,
              yes: { x: 416.5, y: 506.5, size: 6 },
              no: { x: 431.5, y: 506.5, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FISHERY, //  漁業
            },
            {
              key: "unknown", // ①派遣元について、漁業又は漁業に関連する業務を行っている場合
              page: 2,
              yes: { x: 416.5, y: 439.5, size: 6 },
              no: { x: 431.5, y: 439.5, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FISHERY, //  漁業
            },
            {
              key: "unknown", // ②派遣元について、地方公共団体等が資本金の過半数を出資している機関である場合
              page: 2,
              yes: { x: 416.5, y: 366.5, size: 6 },
              no: { x: 431.5, y: 366.5, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FISHERY, //  漁業
            },
            {
              key: "unknown", // ③派遣元について、地方公共団体の職員等が役員として在籍している場合
              page: 2,
              yes: { x: 416.5, y: 283, size: 6 },
              no: { x: 431.5, y: 283, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FISHERY, //  漁業
            },
            {
              key: "unknown", // ④派遣元について、地方公共団体等が実質的に業務執行に関与している場合
              page: 2,
              yes: { x: 416.5, y: 199, size: 6 },
              no: { x: 431.5, y: 199, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FISHERY, //  漁業
            },
            {
              key: "unknown", // 労働者派遣事業許可証の写し
              page: 2,
              yes: { x: 416.5, y: 126.5, size: 6 },
              no: { x: 431.5, y: 126.5, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FISHERY, //  漁業
            },
            {
              key: "unknown", // 派遣計画書
              page: 2,
              yes: { x: 416.5, y: 87, size: 6 },
              no: { x: 431.5, y: 87, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FISHERY, //  漁業
            },
            {
              key: "unknown", // 労働者派遣契約書の写し
              page: 2,
              yes: { x: 416.5, y: 65, size: 6 },
              no: { x: 431.5, y: 65, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FISHERY, //  漁業
            },
            {
              key: "unknown", // 就業条件明示書の写し
              page: 2,
              yes: { x: 416.5, y: 42, size: 6 },
              no: { x: 431.5, y: 42, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FISHERY, //  漁業
            },
            // Page 3
            {
              key: "unknown", // 協議会の構成員であることの証明書（特定技能所属機関）
              page: 3,
              yes: { x: 416.5, y: 745, size: 6 },
              no: { x: 431.5, y: 745, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FISHERY, //  漁業
            },
            {
              key: "unknown", // 漁業分野における特定技能外国人の受入れに関する誓約書（登録支援機関）
              page: 3,
              yes: { x: 416.5, y: 694.5, size: 6 },
              no: { x: 431.5, y: 694.5, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FISHERY, //  漁業
            },
            // Page 4
            {
              key: "unknown", // 派遣先の概要書（漁業分野）
              page: 4,
              yes: { x: 416.5, y: 722, size: 6 },
              no: { x: 431.5, y: 722, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FISHERY, //  漁業
            },
            {
              key: "unknown", // ①労働保険の適用事業所で特定技能外国人を初めて受け入れる場合
              page: 4,
              yes: { x: 416.5, y: 684.5, size: 6 },
              no: { x: 431.5, y: 684.5, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FISHERY, //  漁業
            },
            {
              key: "unknown", // 以下のいずれかの書類
              page: 4,
              yes: { x: 416.5, y: 617.5, size: 6 },
              no: { x: 431.5, y: 617.5, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FISHERY, //  漁業
            },
            {
              key: "unknown", // 上記に対応する以下のいずれかの書類
              page: 4,
              yes: { x: 416.5, y: 540, size: 6 },
              no: { x: 431.5, y: 540, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FISHERY, //  漁業
            },
            {
              key: "unknown", // 以下のいずれかの書類
              page: 4,
              yes: { x: 416.5, y: 416.5, size: 6 },
              no: { x: 431.5, y: 416.5, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FISHERY, //  漁業
            },
            {
              key: "unknown", // 納税証明書（その３）
              page: 4,
              yes: { x: 416.5, y: 243.5, size: 6 },
              no: { x: 431.5, y: 243.5, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FISHERY, //  漁業
            },
            // Page 5
            {
              key: "unknown", // 法人住民税の納税証明書
              page: 5,
              yes: { x: 416.5, y: 672.5, size: 6 },
              no: { x: 431.5, y: 672.5, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FISHERY, //  漁業
            },
            {
              key: "unknown", // 公的義務履行に関する説明書
              page: 5,
              yes: { x: 416.5, y: 571.5, size: 6 },
              no: { x: 431.5, y: 571.5, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FISHERY, //  漁業
            },
            // Page 6
            {
              key: "unknown", // ①所属機関が許可又は免許を受けて漁業若しくは養殖業を営んでいる場合
              page: 6,
              yes: { x: 416.5, y: 704, size: 6 },
              no: { x: 431.5, y: 704, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FISHERY, //  漁業
            },
            {
              key: "unknown", // ②所属機関が漁業協同組合に所属して漁業又は養殖業を営んでいる場合
              page: 6,
              yes: { x: 416.5, y: 598.5, size: 6 },
              no: { x: 431.5, y: 598.5, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FISHERY, //  漁業
            },
            {
              key: "unknown", // 以下のいずれかの書類
              page: 6,
              yes: { x: 416.5, y: 506.5, size: 6 },
              no: { x: 431.5, y: 506.5, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FISHERY, //  漁業
            },
            {
              key: "unknown", // ①派遣元について、漁業又は漁業に関連する業務を行っている場合
              page: 6,
              yes: { x: 416.5, y: 439.5, size: 6 },
              no: { x: 431.5, y: 439.5, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FISHERY, //  漁業
            },
            {
              key: "unknown", // ②派遣元について、地方公共団体等が資本金の過半数を出資している機関である場合
              page: 6,
              yes: { x: 416.5, y: 366.5, size: 6 },
              no: { x: 431.5, y: 366.5, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FISHERY, //  漁業
            },
            {
              key: "unknown", // ③派遣元について、地方公共団体の職員等が役員として在籍している場合
              page: 6,
              yes: { x: 416.5, y: 282.5, size: 6 },
              no: { x: 431.5, y: 282.5, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FISHERY, //  漁業
            },
            {
              key: "unknown", // ④派遣元について、地方公共団体等が実質的に業務執行に関与している場合
              page: 6,
              yes: { x: 416.5, y: 199, size: 6 },
              no: { x: 431.5, y: 199, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FISHERY, //  漁業
            },
            {
              key: "unknown", // 労働者派遣事業許可証の写し
              page: 6,
              yes: { x: 416.5, y: 126, size: 6 },
              no: { x: 431.5, y: 126, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FISHERY, //  漁業
            },
            {
              key: "unknown", // 派遣計画書
              page: 6,
              yes: { x: 416.5, y: 87, size: 6 },
              no: { x: 431.5, y: 87, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FISHERY, //  漁業
            },
            {
              key: "unknown", // 労働者派遣契約書の写し
              page: 6,
              yes: { x: 416.5, y: 65, size: 6 },
              no: { x: 431.5, y: 65, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FISHERY, //  漁業
            },
            {
              key: "unknown", // 就業条件明示書の写し
              page: 6,
              yes: { x: 416.5, y: 42.5, size: 6 },
              no: { x: 431.5, y: 42.5, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FISHERY, //  漁業
            },
            // Page 7
            {
              key: "unknown", // 協議会の構成員であることの証明書（特定技能所属機関）
              page: 7,
              yes: { x: 416.5, y: 745, size: 6 },
              no: { x: 431.5, y: 745, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FISHERY, //  漁業
            },
            {
              key: "unknown", // 漁業分野における特定技能外国人の受入れに関する誓約書（登録支援機関）
              page: 7,
              yes: { x: 416.5, y: 694.5, size: 6 },
              no: { x: 431.5, y: 694.5, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FISHERY, //  漁業
            },
            // Page 8
            {
              key: "unknown", // 派遣先の概要書（漁業分野）
              page: 8,
              yes: { x: 416.5, y: 722.5, size: 6 },
              no: { x: 431.5, y: 722.5, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FISHERY, //  漁業
            },
            {
              key: "unknown", // 労働保険料等納付証明書（未納なし証明）
              page: 8,
              yes: { x: 416.5, y: 683, size: 6 },
              no: { x: 431.5, y: 683, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FISHERY, //  漁業
            },
            {
              key: "unknown", // 以下のいずれかの書類
              page: 8,
              yes: { x: 416.5, y: 622, size: 6 },
              no: { x: 431.5, y: 622, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FISHERY, //  漁業
            },
            {
              key: "unknown", // 上記に対応する以下のいずれかの書類
              page: 8,
              yes: { x: 416.5, y: 549, size: 6 },
              no: { x: 431.5, y: 549, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FISHERY, //  漁業
            },
            {
              key: "unknown", // （該当する場合はチェック）
              page: 8,
              yes: { x: 416.5, y: 493.5, size: 6 },
              no: { x: 431.5, y: 493.5, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FISHERY, //  漁業
            },
            // Page 9
            {
              key: "unknown", // 以下のいずれかの書類
              page: 9,
              yes: { x: 416.5, y: 672.5, size: 6 },
              no: { x: 431.5, y: 672.5, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FISHERY, //  漁業
            },
            {
              key: "unknown", // 個人事業主の国民健康保険被保険者証の写し
              page: 9,
              yes: { x: 416.5, y: 538, size: 6 },
              no: { x: 431.5, y: 538, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FISHERY, //  漁業
            },
            {
              key: "unknown", // 個人事業主の国民健康保険料（税）納付証明書
              page: 9,
              yes: { x: 416.5, y: 392.5, size: 6 },
              no: { x: 431.5, y: 392.5, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FISHERY, //  漁業
            },
            {
              key: "unknown", // 個人事業主の被保険者記録照会回答票
              page: 9,
              yes: { x: 416.5, y: 230.5, size: 6 },
              no: { x: 431.5, y: 230.5, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FISHERY, //  漁業
            },
            {
              key: "unknown", // 以下のいずれかの書類
              page: 9,
              yes: { x: 416.5, y: 102, size: 6 },
              no: { x: 431.5, y: 102, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FISHERY, //  漁業
            },
            // Page 10
            {
              key: "unknown", // 個人事業主の納税証明書（その３）
              page: 10,
              yes: { x: 416.5, y: 649, size: 6 },
              no: { x: 431.5, y: 649, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FISHERY, //  漁業
            },
            {
              key: "unknown", // 個人事業主の個人住民税の納税証明書
              page: 10,
              yes: { x: 416.5, y: 459.5, size: 6 },
              no: { x: 431.5, y: 459.5, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FISHERY, //  漁業
            },
            {
              key: "unknown", // 公的義務履行に関する説明書
              page: 10,
              yes: { x: 416.5, y: 359.5, size: 6 },
              no: { x: 431.5, y: 359.5, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FISHERY, //  漁業
            },
          ];
          break;
      }
    } else if (renewVisaEmployeeApplication?.employeeInfoTwoResidenceStatusId?.toString() === "2") {
      switch (tableKey) {
        case "table_1":
          templatePdfUrl = "/pdf/renew_visa_table_1_skill_2.pdf";
          divisions = [COMPANY_DIVISION.CORPORATE, COMPANY_DIVISION.SOLE_PROPRIETOR];
          fileFields = [
            {
              key: "unknown", // 特定技能外国人の在留諸申請に係る提出書類一覧表(本表)
              page: 1,
              yes: { x: 445, y: 758, size: 6 },
              no: { x: 464, y: 758, size: 6 },
              fixedValue: "yes",
              inputArea: { x: 474, y: 728, width: 72.5, height: 58 },
            },
            {
              key: "unknown", // 在留期間更新許可申請書
              page: 1,
              yes: { x: 445, y: 704, size: 6 },
              no: { x: 464, y: 704, size: 6 },
              fixedValue: "no",
            },
            {
              key: "foreignWorkerCompensationStatementFilePaths", // 特定技能外国人の報酬に関する説明書
              page: 1,
              yes: { x: 445, y: 654, size: 6 },
              no: { x: 464, y: 654, size: 6 },
              inputArea: { x: 474, y: 628, width: 72.5, height: 50.5 },
            },
            {
              key: "employmentContractCopyFilePath", // 特定技能雇用契約書の写し
              page: 1,
              yes: { x: 445, y: 616, size: 6 },
              no: { x: 464, y: 616, size: 6 },
            },
            {
              key: "employmentConditionsCopyFilePaths", // 雇用条件書の写し
              page: 1,
              yes: { x: 445, y: 565, size: 6 },
              no: { x: 464, y: 565, size: 6 },
            },
            {
              key: "wagePaymentFilePath", // 賃金の支払
              page: 1,
              yes: { x: 445, y: 513, size: 6 },
              no: { x: 464, y: 513, size: 6 },
            },
            {
              key: "applicantIndividualResidentTaxCertFilePath", // 申請人の個人住民税の課税証明書
              page: 1,
              yes: { x: 445, y: 485, size: 6 },
              no: { x: 464, y: 485, size: 6 },
              inputArea: { x: 474, y: 469, width: 72.5, height: 31 },
            },
            {
              key: "applicantResidentTaxPaymentCertFilePath", // 申請人の住民税の納税証明書
              page: 1,
              yes: { x: 445, y: 440, size: 6 },
              no: { x: 464, y: 440, size: 6 },
              inputArea: { x: 474, y: 409, width: 72.5, height: 60 },
            },
            {
              key: "applicantWithholdingSlipCopyFilePath", // 申請人の給与所得の源泉徴収票の写し
              page: 1,
              yes: { x: 445, y: 345, size: 6 },
              no: { x: 464, y: 345, size: 6 },
              inputArea: { x: 474, y: 281, width: 72.5, height: 128.5 },
            },
            {
              key: "applicantHealthInsuranceCardCopyFilePath", // 申請人の国民健康保険被保険者証の写し
              page: 1,
              yes: { x: 445, y: 262, size: 6 },
              no: { x: 464, y: 262, size: 6 },
              inputArea: { x: 474, y: 242, width: 72.5, height: 39 },
            },
            {
              key: "applicantHealthInsurancePaymentCertFilePath", // 申請人の国民健康保険料(税)納付証明書
              page: 1,
              yes: { x: 445, y: 217, size: 6 },
              no: { x: 464, y: 217, size: 6 },
              inputArea: { x: 474, y: 191.5, width: 72.5, height: 50.5 },
            },
            {
              key: "applicantPensionReceiptCopy1FilePath",
              page: 1,
              yes: { x: 445, y: 130.5, size: 6 },
              no: { x: 464, y: 130.5, size: 6 },
              inputArea: { x: 474, y: 68, width: 72.5, height: 123 },
              children: [
                "applicantPensionReceiptCopy1FilePath", // 申請人の被保険者記録照会（納付Ⅱ）
                "applicantPensionRecordInquiry2FilePath", //申請日の属する月の前々月までの２４か月分の申請人の国民年金保険料領収証書の写し
              ],
            },
            {
              key: "previousApplicationDocumentsFilePath", // 前回申請時に履行すべきであった公的義務に係る書類
              page: 1,
              yes: { x: 445, y: 47, size: 6 },
              no: { x: 464, y: 47, size: 6 },
            },
            {
              key: "publicDutyPledgeFilePath", // 公的義務履行に関する誓約書
              page: 2,
              yes: { x: 445, y: 771, size: 6 },
              no: { x: 464, y: 771, size: 6 },
            },
          ];
          break;
        case "table_2_1":
          templatePdfUrl = "/pdf/renew_visa_table_2_1_skill_1.pdf";
          divisions = [COMPANY_DIVISION.CORPORATE, COMPANY_DIVISION.SOLE_PROPRIETOR];
          fileFields = [
            {
              key: "quarterlyReportCopyFilePath", //四季報の写し又は日本の証券取引所に上場していることを証明する文書（写し）
              page: 1,
              yes: { x: 436.5, y: 707, size: 6 },
              no: { x: 454.5, y: 707, size: 6 },
            },
            {
              key: "innovationCompanyProofFilePath", //高度専門職省令第１条第１項各
              page: 1,
              yes: { x: 436.5, y: 642, size: 6 },
              no: { x: 454.5, y: 642, size: 6 },
            },
            {
              // key: "innovationCompanyProofFilePath", //高度専門職省令第１条第１項各
              key: "certifiedCompanyProofFilePath", //「一定の条件を満たす企業等」
              page: 1,
              yes: { x: 436.5, y: 564.5, size: 6 },
              no: { x: 454.5, y: 564.5, size: 6 },
            },
            {
              key: "annualSalaryReportCopyFilePath", // 前年分の職員の給与所得の源泉
              page: 1,
              yes: { x: 436.5, y: 478.5, size: 6 },
              no: { x: 454.5, y: 478.5, size: 6 },
            },
            {
              key: "immigrationElectronicSystemPledgeFilePath", //出入国在留管理庁電子届出シス
              page: 1,
              yes: { x: 436.5, y: 381.5, size: 6 },
              no: { x: 454.5, y: 381.5, size: 6 },
            },
            {
              key: "documentOmissionPledgeFilePath", //書類の省略に当たっての誓約書
              page: 1,
              yes: { x: 436.5, y: 307, size: 6 },
              no: { x: 454.5, y: 307, size: 6 },
            },
          ];
          break;
        case "table_2_2":
          templatePdfUrl = "/pdf/renew_visa_table_2_2_skill_2.pdf";
          divisions = [COMPANY_DIVISION.CORPORATE];
          fileFields = [
            {
              key: "specificSkillsInstitutionOverviewDocumentFilePath", // 特定技能所属機関概要書
              page: 1,
              yes: { x: 436.5, y: 729, size: 6 },
              no: { x: 454.5, y: 729, size: 6 },
              inputArea: { x: 463.5, y: 701, width: 68, height: 54 },
            },
            {
              key: "registrationCertFilePath", // 登記事項証明書
              page: 1,
              yes: { x: 436.5, y: 675, size: 6 },
              no: { x: 454.5, y: 675, size: 6 },
              inputArea: { x: 464, y: 646, width: 68, height: 56 },
            },
            {
              key: "executiveResidentRecordCopyFilePath", // 業務執行に関与する役員の住民票の写し
              page: 1,
              yes: { x: 436.5, y: 620, size: 6 },
              no: { x: 454.5, y: 620, size: 6 },
              inputArea: { x: 464, y: 591, width: 68, height: 56 },
            },
            {
              key: "specificSkillsInstitutionOfficerAffidavitFilePath", // 特定技能所属機関の役員に関する誓約書
              page: 1,
              yes: { x: 436.5, y: 565, size: 6 },
              no: { x: 454.5, y: 565, size: 6 },
              inputArea: { x: 464, y: 533.5, width: 68, height: 58 },
            },
            {
              key: "laborInsuranceNoDelegationFilePath", // 労働保険事務組合に事務委託していない場合
              page: 1,
              yes: { x: 436.5, y: 477, size: 6 },
              no: { x: 454.5, y: 477, size: 6 },
              inputArea: { x: 464, y: 440, width: 68, height: 74 },
            },
            {
              key: "laborInsuranceDelegationFilePath", // 労働保険事務組合に事務委託している場合
              page: 1,
              yes: { x: 436.5, y: 403, size: 6 },
              no: { x: 454.5, y: 403, size: 6 },
              inputArea: { x: 464, y: 365, width: 68, height: 74 },
            },
            {
              key: "socialInsurancePaymentStatusCertFilePath", // 社会保険料納入状況回答票又は健康保険・厚生年金保険料領収証書の写し
              page: 1,
              yes: { x: 436.5, y: 335.5, size: 6 },
              no: { x: 454.5, y: 335.5, size: 6 },
              inputArea: { x: 464, y: 305, width: 68, height: 59 },
            },
            {
              key: "taxOfficePaymentCertNo3FilePath", // 税務署発行の納税証明書(その３)
              page: 1,
              yes: { x: 436.5, y: 274, size: 6 },
              no: { x: 454.5, y: 274, size: 6 },
              inputArea: { x: 464, y: 242, width: 68, height: 64 },
            },
            {
              key: "corporateResidentTaxPaymentCertFilePath", // 法人住民税の市町村発行の納税証明書
              page: 1,
              yes: { x: 436.5, y: 210, size: 6 },
              no: { x: 454.5, y: 210, size: 6 },
              inputArea: { x: 464, y: 177, width: 68, height: 64 },
            },
            {
              key: "publicDutyComplianceFilePath", // 公的義務履行に関する説明書
              page: 1,
              yes: { x: 436.5, y: 157, size: 6 },
              no: { x: 454.5, y: 157, size: 6 },
              inputArea: { x: 464, y: 137, width: 68, height: 40 },
            },
          ];
          break;
        case "table_2_3":
          templatePdfUrl = "/pdf/renew_visa_table_2_3_skill_2.pdf";
          divisions = [COMPANY_DIVISION.SOLE_PROPRIETOR];
          fileFields = [
            {
              key: "specificSkillsInstitutionOverviewDocumentFilePath", // 特定技能所属機関概要書
              page: 1,
              yes: { x: 436.5, y: 728, size: 6 },
              no: { x: 454.5, y: 728, size: 6 },
              inputArea: { x: 464, y: 699, width: 68, height: 55 },
            },
            {
              key: "individualBusinessOwnerResidentCertFilePath", // 個人事業主の住民票の写し
              page: 1,
              yes: { x: 436.5, y: 674, size: 6 },
              no: { x: 454.5, y: 674, size: 6 },
              inputArea: { x: 464, y: 646, width: 68, height: 54 },
            },
            {
              key: "laborInsuranceNoDelegationFilePath", // 労働保険事務組合に事務委託していない場合
              page: 1,
              yes: { x: 436.5, y: 592, size: 6 },
              no: { x: 454.5, y: 592, size: 6 },
              inputArea: { x: 464, y: 554, width: 68, height: 76 },
            },
            {
              key: "laborInsuranceDelegationFilePath", // 労働保険事務組合に事務委託している場合
              page: 1,
              yes: { x: 436.5, y: 517, size: 6 },
              no: { x: 454.5, y: 517, size: 6 },
              inputArea: { x: 464, y: 479, width: 68, height: 76 },
            },
            {
              key: "healthInsuranceWelfarePensionCertFilePath", // 健康保険・厚生年金保険の適用事業所の場合
              page: 1,
              yes: { x: 436.5, y: 425.5, size: 6 },
              no: { x: 454.5, y: 425.5, size: 6 },
              inputArea: { x: 464, y: 387, width: 68, height: 76 },
            },
            {
              key: "nonHealthInsuranceWelfarePensionCertFilePath", // 個人事業主の国民健康保険被保険者証の写し
              page: 1,
              yes: { x: 436.5, y: 352.5, size: 6 },
              no: { x: 454.5, y: 352.5, size: 6 },
              inputArea: { x: 464, y: 316.5, width: 68, height: 72 },
            },
            {
              key: "individualHealthInsurancePaymentCertFilePath", // 個人事業主の国民健康保険料(税)納付証明書
              page: 1,
              yes: { x: 436.5, y: 266, size: 6 },
              no: { x: 454.5, y: 266, size: 6 },
              inputArea: { x: 464, y: 216, width: 68, height: 102 },
            },
            {
              key: "individualPensionReceiptCopyFilePath", // 個人事業主の国民年金保険料領収証書の写し又は被保険者記録照会(納付Ⅱ)
              page: 1,
              yes: { x: 436.5, y: 161, size: 6 },
              no: { x: 454.5, y: 161, size: 6 },
              inputArea: { x: 464, y: 108, width: 68, height: 106 },
            },
            // page 2
            {
              key: "individualBusinessOwnerTaxOfficePaymentCertNo3FilePath", // 個人事業主の税務署発行の納税証明書(その３)
              page: 2,
              yes: { x: 436.5, y: 721.5, size: 6 },
              no: { x: 454.5, y: 721.5, size: 6 },
              inputArea: { x: 464, y: 687.5, width: 68, height: 65 },
            },
            {
              key: "individualResidentTaxCertFilePath", // 個人事業主の個人住民税の市町村発行の納税証明書
              page: 2,
              yes: { x: 436.5, y: 655.5, size: 6 },
              no: { x: 454.5, y: 655.5, size: 6 },
              inputArea: { x: 464, y: 622, width: 68, height: 64 },
            },
            {
              key: "publicDutyComplianceFilePath", // 公的義務履行に関する説明書
              page: 2,
              yes: { x: 436.5, y: 600, size: 6 },
              no: { x: 454.5, y: 600, size: 6 },
              inputArea: { x: 464, y: 576.5, width: 68, height: 44 },
            },
          ];
          break;
        case "table_3_1":
          templatePdfUrl = "/pdf/renew_visa_table_3_1_skill_2.pdf";
          divisions = [COMPANY_DIVISION.CORPORATE, COMPANY_DIVISION.SOLE_PROPRIETOR];
          fileFields = [
            {
              key: "specificSkillOrganizationOverviewFilePath",
              page: 1,
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.BUILDING_CLEANING, //   ビルクリーニング
              yes: { x: 442, y: 725.5, size: 6 },
              no: { x: 459.5, y: 725.5, size: 6 },
              inputArea: { x: 468, y: 695, width: 66, height: 61 },
              children: [
                "specificSkillOrganizationOverviewFilePath", // ①建築物清掃業登録証明書
                "buildingEnvHealthMgmtRegistrationCertFilePath", // ②建築物環境衛生総合管理業登録証明書
              ],
            },
            {
              key: "councilMembershipCertFilePath", // 協議会の構成員であることの証明書
              page: 1,
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.BUILDING_CLEANING, //   ビルクリーニング
              yes: { x: 442, y: 681, size: 6 },
              no: { x: 459.5, y: 681, size: 6 },
            },
            {
              key: "councilMembershipCertFilePath", // 協議会の構成員であることの証明書
              page: 1,
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.INDUSTRIAL_PRODUCT_MANUFACTURING, //   工業製品製造業 = 素形材・産業機械・電気電子情報関連製造業
              yes: { x: 442, y: 634.8, size: 6 },
              no: { x: 459.5, y: 634.8, size: 6 },
            },
            {
              key: "shipbuildingIndustryConfirmationFilePath", // 造船・舶用工業事業者の確認通知書
              page: 1,
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.SHIPBUILDING, //   造船・舶用工業
              yes: { x: 442, y: 590, size: 6 },
              no: { x: 459.5, y: 590, size: 6 },
              inputArea: { x: 468, y: 573, width: 66, height: 30.5 },
            },
            {
              key: "councilMembershipCertFilePath", // 協議会の構成員であることの証明書
              page: 1,
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.SHIPBUILDING, //   造船・舶用工業
              yes: { x: 442, y: 558, size: 6 },
              no: { x: 459.5, y: 558, size: 6 },
            },
            {
              key: "specifiedSkillAutomobileMaintenanceCertFilePath",
              page: 1,
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AUTOMOBILE_MAINTENANCE, //   自動車整備
              yes: { x: 442, y: 512, size: 6 },
              no: { x: 459.5, y: 512, size: 6 },
              children: [
                "specifiedSkillAutomobileMaintenanceCertFilePath", //①自動車整備分野特定技能協議会入会届出書兼構成員資格証明書（受付印があるもの）
                "specifiedSkillAutomobileMaintenanceIssuranceFilePath", //②自動車整備分野特定技能協議会構成員資格証明書発行申請書（受付印があるもの）
              ],
            },
            {
              key: "councilMembershipCertFilePath", // 協議会の構成員であることの証明書
              page: 1,
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AVIATION, // 航空
              yes: { x: 442, y: 467, size: 6 },
              no: { x: 459.5, y: 467, size: 6 },
            },
            {
              key: "hotelBusinessLicenseFilePath", // 旅館業許可証(旅館・ホテル営業許可書)の写し
              page: 1,
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.ACCOMMODATION, // 航空
              yes: { x: 442, y: 438, size: 6 },
              no: { x: 459.5, y: 438, size: 6 },
              inputArea: { x: 468, y: 420.5, width: 66, height: 30.5 },
            },
            {
              key: "councilMembershipCertFilePath", // 協議会の構成員であることの証明書
              page: 1,
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.ACCOMMODATION, // 航空
              yes: { x: 442, y: 405.5, size: 6 },
              no: { x: 459.5, y: 405.5, size: 6 },
            },
            {
              key: "councilMembershipCertFilePath", // 協議会の構成員であることの証明書
              page: 1,
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FOOD_AND_BEVERAGE_MANUFACTURING, // 飲食料品製造業
              yes: { x: 442, y: 372, size: 6 },
              no: { x: 459.5, y: 372, size: 6 },
            },
            {
              key: "businessLicenseOrNotificationFormFilePath", // 保健所長の営業許可証又は届出書の写し
              page: 1,
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FOOD_SERVICE, // 外食業
              yes: { x: 442, y: 289, size: 6 },
              no: { x: 459.5, y: 289, size: 6 },
              inputArea: { x: 468, y: 224, width: 66, height: 127.5 },
            },
            {
              key: "councilMembershipCertFilePath", // 協議会の構成員であることの証明書
              page: 1,
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FOOD_SERVICE, // 外食業
              yes: { x: 442, y: 208.5, size: 6 },
              no: { x: 459.5, y: 208.5, size: 6 },
            },
          ];
          break;
        case "table_3_2":
          templatePdfUrl = "/pdf/renew_visa_table_3_2_skill_2.pdf";
          divisions = [COMPANY_DIVISION.CORPORATE, COMPANY_DIVISION.SOLE_PROPRIETOR];
          fileFields = [
            {
              key: "councilMembershipCertFilePath", // 協議会の構成員であることの証明書(特定技能所属機関)
              page: 1,
              yes: { x: 437, y: 741.2, size: 6 },
              no: { x: 455, y: 741.2, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            // page 2
            {
              key: "unknown", // 農業又は農業に関連する業務を行っている場合
              page: 2,
              yes: { x: 436.5, y: 651, size: 6 },
              no: { x: 454.5, y: 651, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 地方公共団体等が出資（資本金の過半数）している機関である場合
              page: 2,
              yes: { x: 436.5, y: 569.7, size: 6 },
              no: { x: 454.5, y: 569.7, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 地方公共団体の職員等が役員として在籍している場合
              page: 2,
              yes: { x: 436.5, y: 496, size: 6 },
              no: { x: 454.5, y: 496, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 地方公共団体等が実質的に業務執行に関与している場合
              page: 2,
              yes: { x: 436.5, y: 424.5, size: 6 },
              no: { x: 454.5, y: 424.5, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 国家戦略特別区域法に規定する特定機関である場合
              page: 2,
              yes: { x: 436.5, y: 336, size: 6 },
              no: { x: 454.5, y: 336, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 農業分野において派遣形態で特定技能外国人の受入れ
              page: 2,
              yes: { x: 436.5, y: 266, size: 6 },
              no: { x: 454.5, y: 266, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 労働者派遣事業許可証の写し
              page: 2,
              yes: { x: 436.5, y: 230.5, size: 6 },
              no: { x: 454.5, y: 230.5, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 派遣計画書
              page: 2,
              yes: { x: 436.5, y: 190, size: 6 },
              no: { x: 454.5, y: 190, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 労働者派遣契約書の写し
              page: 2,
              yes: { x: 436.5, y: 152, size: 6 },
              no: { x: 454.5, y: 152, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 就業条件明示書の写し
              page: 2,
              yes: { x: 436.5, y: 115.5, size: 6 },
              no: { x: 454.5, y: 115.5, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            // page 3
            {
              key: "unknown", // 派遣先の概要書（農業分野）
              page: 3,
              yes: { x: 436.5, y: 713, size: 6 },
              no: { x: 454.5, y: 713, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 派遣先事業者誓約書
              page: 3,
              yes: { x: 436.5, y: 680, size: 6 },
              no: { x: 454.5, y: 680, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 労働保険料等納付証明書(未納なし証明)
              page: 3,
              yes: { x: 436.5, y: 610, size: 6 },
              no: { x: 454.5, y: 610, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 労働保険概算・増加概算・確定
              page: 3,
              yes: { x: 436.5, y: 554.5, size: 6 },
              no: { x: 454.5, y: 554.5, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 労働保険事務組合が発行した直
              page: 3,
              yes: { x: 436.5, y: 476.7, size: 6 },
              no: { x: 454.5, y: 476.7, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 社会保険料納入状況回答票又は健康保険・厚生年金保
              page: 3,
              yes: { x: 436.5, y: 374.6, size: 6 },
              no: { x: 454.5, y: 374.6, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 税務署発行の納税証明書(その３)
              page: 3,
              yes: { x: 436.5, y: 305.5, size: 6 },
              no: { x: 454.5, y: 305.5, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 法人住民税の市町村発行の納税証明書
              page: 3,
              yes: { x: 436.5, y: 232, size: 6 },
              no: { x: 454.5, y: 232, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 公的義務履行に関する説明書
              page: 3,
              yes: { x: 436.5, y: 189.4, size: 6 },
              no: { x: 454.5, y: 189.4, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 公的義務履行に関する説明書
              page: 3,
              yes: { x: 436.5, y: 146.6, size: 6 },
              no: { x: 454.5, y: 146.6, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            // page 4
            {
              key: "unknown", // 派遣先の概要書（農業分野）
              page: 4,
              yes: { x: 436.5, y: 714.5, size: 6 },
              no: { x: 454.5, y: 714.5, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 派遣先事業者誓約書
              page: 4,
              yes: { x: 436.5, y: 683, size: 6 },
              no: { x: 454.5, y: 683, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 労働保険料等納付証明書
              page: 4,
              yes: { x: 436.5, y: 614, size: 6 },
              no: { x: 454.5, y: 614, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 労働保険概算・増加概算・確定
              page: 4,
              yes: { x: 436.5, y: 561, size: 6 },
              no: { x: 454.5, y: 561, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 労働保険事務組合が発行した直
              page: 4,
              yes: { x: 436.5, y: 486.3, size: 6 },
              no: { x: 454.5, y: 486.3, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 社会保険料納入状況回答票又は
              page: 4,
              yes: { x: 436.5, y: 368, size: 6 },
              no: { x: 454.5, y: 368, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 個人事業主の国民健康保険被保
              page: 4,
              yes: { x: 436.5, y: 301, size: 6 },
              no: { x: 454.5, y: 301, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 個人事業主の国民健康保険料
              page: 4,
              yes: { x: 436.5, y: 218.5, size: 6 },
              no: { x: 454.5, y: 218.5, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 個人事業主の国民年金保険料領
              page: 4,
              yes: { x: 436.5, y: 122, size: 6 },
              no: { x: 454.5, y: 122, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            // page 5
            {
              key: "unknown", // 個人事業主の税務署発行の納税証明書
              page: 5,
              yes: { x: 436.5, y: 699, size: 6 },
              no: { x: 454.5, y: 699, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 個人事業主の個人住民税の市町村発行の納税証明書(注)直近１年分が必要
              page: 5,
              yes: { x: 436.5, y: 619.7, size: 6 },
              no: { x: 454.5, y: 619.7, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 個人事業主の個人住民税の市町村発行の納税証明書(注)直近２年分が必要
              page: 5,
              yes: { x: 436.5, y: 568, size: 6 },
              no: { x: 454.5, y: 568, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
            {
              key: "unknown", // 公的義務履行に関する説明書
              page: 5,
              yes: { x: 436.5, y: 515.8, size: 6 },
              no: { x: 454.5, y: 515.8, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.AGRICULTURE, //  農業
            },
          ];
          break;
        case "table_3_3":
          templatePdfUrl = "/pdf/renew_visa_table_3_3_skill_2_edit.pdf";
          divisions = [COMPANY_DIVISION.CORPORATE, COMPANY_DIVISION.SOLE_PROPRIETOR];
          fileFields = [
            {
              key: "fishingLicenseCopy1FilePath",
              page: 1,
              yes: { x: 437, y: 705.3, size: 6 },
              no: { x: 455, y: 705.3, size: 6 },
              inputArea: { x: 463.5, y: 655, width: 69, height: 98.5 },
              children: [
                "fishingLicenseCopy1FilePath", // ①許可証の写し
                "fishingLicenseCopy2FilePath", // ②免許の指令書の写し
                "fishingLicenseCopy3FilePath", // ③その他許可または免許を受け
              ],
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FISHERY, //  漁業
            },
            {
              key: "fishingCooperativeRightsDocumentFilePath",
              page: 1,
              yes: { x: 437, y: 601.2, size: 6 },
              no: { x: 455, y: 601.2, size: 6 },
              inputArea: { x: 463.5, y: 544.5, width: 69, height: 111 },
              children: [
                "fishingCooperativeRightsDocumentFilePath", // ①当該組合の漁業権の内容たる漁業又は養殖業を営むことを確認できる当該組合が発行した書類の写し
                "fishingCooperativeMembershipDocumentFilePath", // ②その他当該組合に所属して漁業又は養殖業を営んでいることが確認できる書類の写し
              ],
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FISHERY, //  漁業
            },
            {
              key: "fishingBoatRegistryCopyFilePath",
              page: 1,
              yes: { x: 437, y: 519.5, size: 6 },
              no: { x: 455, y: 519.5, size: 6 },
              inputArea: { x: 463.5, y: 492, width: 69, height: 53 },
              children: [
                "fishingBoatRegistryCopyFilePath", // ①漁船原簿謄本の写し
                "fishingBoatRegistrationCertCopyFilePath", // ②漁船登録票の写し
              ],
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FISHERY, //  漁業
            },
            {
              key: "councilMembershipCertFilePath", // 協議会の構成員であることの証明書
              page: 1,
              yes: { x: 437, y: 475.6, size: 6 },
              no: { x: 455, y: 475.6, size: 6 },
              companySpecificIndustryName: COMPANY_SPECIFIC_INDUSTRY_NAME.FISHERY, //  漁業
            },

            // page 2
            // page 3
            // page 4
            // page 5
          ];
          break;
      }
    }

    const pdfTableRows: TCheckFileFieldResult[] = [];
    const mergedFiles: TMergeRenewVisaFile[] = [];

    for (const fileField of fileFields) {
      const isMatchedCompanySpecificIndustry =
        !fileField.companySpecificIndustryName || fileField.companySpecificIndustryName === companySpecificIndustryName;
      const isMatchedDivision = !divisions || (companyDivision && divisions.includes(companyDivision));
      const isMatchedFile = files.some((file) => {
        const isUpdated = !!file.filePath || file.existed;
        const isMatchedKey = file.key === fileField.key;
        const notFixedNo = fileField.fixedValue !== "no";
        let isMatchedChildrenKey = false;
        if (fileField.children && fileField.children.length > 0) {
          isMatchedChildrenKey = fileField.children.some((child) => child == file.key);
        }
        return isUpdated && (isMatchedChildrenKey || isMatchedKey) && isMatchedCompanySpecificIndustry && isMatchedDivision && notFixedNo;
      });
      const isMatchedAll = isMatchedCompanySpecificIndustry && isMatchedDivision && isMatchedFile;

      const pdfTableRow: TCheckFileFieldResult = { ...fileField };
      // Check value yes or no
      if (fileField.fixedValue === "yes" || isMatchedAll) {
        pdfTableRow.value = "yes";
        yesCount++;
        if (fileField.children && fileField.children.length > 0) {
          for (const childKey of fileField.children) {
            const file = files.find((item) => item.key === childKey);
            if (file) {
              mergedFiles.push(file);
            }
          }
        } else {
          const file = files.find((item) => item.key === fileField.key);
          mergedFiles.push(file!);
        }
      } else if (fileField.fixedValue === "no" || !isMatchedAll) {
        pdfTableRow.value = "no";
        noCount++;
      }
      // Check input area
      if (fileField.children && fileField.children.length > 0) {
        const inputs: string[] = [];
        for (const childKey of fileField.children) {
          const file = files.find((item) => item.key === childKey);
          if (file?.input && isMatchedCompanySpecificIndustry && isMatchedDivision) {
            inputs.push(file?.input);
          }
        }
        pdfTableRow.input = inputs.join(", ");
        if (pdfTableRow.input) {
          inputCount++;
        }
      } else {
        const file = files.find((item) => item.key === fileField.key);
        if (file?.input && isMatchedCompanySpecificIndustry && isMatchedDivision) {
          pdfTableRow.input = file.input;
          inputCount++;
        }
      }

      pdfTableRows.push(pdfTableRow);
    }

    return {
      templatePdfUrl,
      pdfTableRows,
      mergedFiles,
      yesCount,
      noCount,
      inputCount,
    };
  };

  const handleCreateFileListTablesPdf = async (
    tableKey: "table_1" | "table_2_1" | "table_2_2" | "table_2_3" | "table_3_1" | "table_3_2" | "table_3_3",
    templatePdfUrl: string,
    pdfTableRows: TCheckFileFieldResult[],
  ) => {
    try {
      // const hasMergedFile = pdfTableRows.find((item) => item.value === "yes");
      const hasMergedFile = true;

      let isShowEvenIfNoMergedFile = false;
      if (tableKey === "table_2_2" && company?.division === COMPANY_DIVISION.CORPORATE) {
        isShowEvenIfNoMergedFile = true;
      }
      if (tableKey === "table_2_3" && company?.division === COMPANY_DIVISION.SOLE_PROPRIETOR) {
        isShowEvenIfNoMergedFile = true;
      }
      if (templatePdfUrl && (hasMergedFile || isShowEvenIfNoMergedFile)) {
        const arrayBuffer = await fetch(templatePdfUrl).then((res) => res.arrayBuffer());
        const bytes = new Uint8Array(arrayBuffer);
        const templatePdf = await PDFDocument.load(bytes, { ignoreEncryption: true });
        const editedPdf = await PDFDocument.create();
        editedPdf.registerFontkit(fontkit);
        if (!fontBytes) return;
        const customFont = await editedPdf.embedFont(fontBytes);
        // Copy pages from raw pdf into final pdf
        const copiedPages = await editedPdf.copyPages(templatePdf, templatePdf.getPageIndices());
        copiedPages.forEach((page) => editedPdf.addPage(page));
        for (const pdfTableRow of pdfTableRows) {
          const page = editedPdf.getPage(pdfTableRow.page - 1);
          if (pdfTableRow.value === "yes") {
            page.drawCircle({
              x: pdfTableRow.yes.x,
              y: pdfTableRow.yes.y,
              size: pdfTableRow.yes.size,
              borderWidth: 1,
              borderColor: rgb(0, 0, 0),
            });
          } else {
            page.drawCircle({
              x: pdfTableRow.no.x,
              y: pdfTableRow.no.y,
              size: pdfTableRow.no.size,
              borderWidth: 1,
              borderColor: rgb(0, 0, 0),
            });
          }

          //// Uncomment code bellow to check circles and rectangles
          // page.drawCircle({
          //   x: pdfTableRow.yes.x,
          //   y: pdfTableRow.yes.y,
          //   size: pdfTableRow.yes.size,
          //   borderWidth: 1,
          //   borderColor: rgb(1, 0, 0),
          // });
          // page.drawCircle({
          //   x: pdfTableRow.no.x,
          //   y: pdfTableRow.no.y,
          //   size: pdfTableRow.no.size,
          //   borderWidth: 1,
          //   borderColor: rgb(1, 0, 0),
          // });
          // if (pdfTableRow.inputArea) {
          //   page.drawRectangle({
          //     x: pdfTableRow.inputArea.x,
          //     y: pdfTableRow.inputArea.y,
          //     width: pdfTableRow.inputArea.width,
          //     height: pdfTableRow.inputArea.height,
          //     borderColor: rgb(1, 0, 0),
          //     borderWidth: 1,
          //   });
          // }

          if (pdfTableRow.input && pdfTableRow.inputArea && pdfTableRow.value === "no") {
            const lines = breakTextIntoLines(pdfTableRow.input, ["", ", ", ".", "。"], pdfTableRow.inputArea.width - 2, (t: string) =>
              customFont.widthOfTextAtSize(t, 7),
            );
            page.drawText(pdfTableRow.input, {
              x: pdfTableRow.inputArea.x + 2,
              y: pdfTableRow.inputArea.y + pdfTableRow.inputArea.height / 2 + (lines.length * 10) / 2 - 7,
              font: customFont,
              size: 7,
              maxWidth: pdfTableRow.inputArea.width - 3,
              lineHeight: 10,
              blendMode: BlendMode.Normal,
              wordBreaks: ["", ", ", ".", "。"],
              color: rgb(0, 0, 0),
            });
          }
        }
        const pdfBytes = await editedPdf.save();
        const blob = new Blob([pdfBytes as BlobPart], { type: "application/pdf" });
        const file = new File([blob], `${tableKey}.pdf`, { type: blob.type });
        return file;
      }
      return null;
    } catch (error) {
      toast.error(API_UNKNOWN_ERROR_MESSAGE);
      console.log(error);
    }
  };

  const handleMergeUploadedPdfs = async (files: TMergeRenewVisaFile[]) => {
    const filePaths = files.map((item) => item?.filePath).filter((item) => !!item);
    const uploadedFiles =
      filePaths.length > 0
        ? await Promise.all(
            filePaths
              .map((filePath) => {
                if (typeof filePath === "string") {
                  return generateFileFromFilePath(filePath!);
                } else if (Array.isArray(filePath)) {
                  return filePath?.map((childFilePath) => generateFileFromFilePath(childFilePath!));
                }
              })
              .flat(),
          )
        : [];
    if (uploadedFiles.length === 0) return null;
    return mergePdfs(uploadedFiles, "merged.pdf");
  };

  const generateMergedPdf = async (
    renewVisaEmployeeApplication: TRenewVisaEmployeeApplicationDocument,
    uploadFileData: TRenewVisaUploadFileDocument,
  ) => {
    const coverPdf = await handleCreateCoverPagePdf(renewVisaEmployeeApplication);

    const { table1Files, table2And1Files, table2And2Files, table2And3Files, table3And1Files, table3And2Files, table3And3Files } =
      await handleGetFilesGroupByTable(renewVisaEmployeeApplication, uploadFileData);

    const tableFileGroups: {
      tableFiles: TMergeRenewVisaFile[];
      tableKey: TTableKey;
    }[] = [
      {
        tableFiles: table1Files,
        tableKey: "table_1",
      },
      {
        tableFiles: table2And1Files,
        tableKey: "table_2_1",
      },
      {
        tableFiles: table2And2Files,
        tableKey: "table_2_2",
      },
      {
        tableFiles: table2And3Files,
        tableKey: "table_2_3",
      },
      {
        tableFiles: table3And1Files,
        tableKey: "table_3_1",
      },

      {
        tableFiles: table3And2Files,
        tableKey: "table_3_2",
      },
      {
        tableFiles: table3And3Files,
        tableKey: "table_3_3",
      },
    ];

    const [table1, table2And1, table2And2, table2And3, table3And1, table3And2, table3And3] = await Promise.all(
      tableFileGroups.map(async (item) => {
        const { templatePdfUrl, pdfTableRows, mergedFiles } = handleCheckFileListTable(renewVisaEmployeeApplication, item.tableKey, item.tableFiles);

        const isShowTemplate = mergedFiles.length > 0 || pdfTableRows.some((row) => row.input);
        const tablePdf = isShowTemplate ? await handleCreateFileListTablesPdf(item.tableKey, templatePdfUrl, pdfTableRows) : null;
        const mergedPdf = tablePdf && isShowTemplate ? await handleMergeUploadedPdfs(mergedFiles) : null;
        return {
          tablePdf,
          mergedPdf,
        };
      }),
    );

    const mergedList = [
      coverPdf,

      table1.tablePdf,
      table1.mergedPdf,

      table2And1.tablePdf,
      table2And1.mergedPdf,

      table2And2.tablePdf,
      table2And2.mergedPdf,

      table2And3.tablePdf,
      table2And3.mergedPdf,

      table3And1.tablePdf,
      table3And1.mergedPdf,

      table3And2.tablePdf,
      table3And2.mergedPdf,

      table3And3.tablePdf,
      table3And3.mergedPdf,
    ];

    const mergedPdf = await mergePdfs(mergedList, "提出書類の一覧表.pdf");

    return mergedPdf;
  };

  return { generateMergedPdf, getFilesGroupByTable: handleGetFilesGroupByTable, checkFileListTable: handleCheckFileListTable };
};
